import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Stack,
  TextField,
  Autocomplete,
  Collapse,
} from '@mui/material';
import {
  ViewKanban as BoardIcon,
  Refresh as RefreshIcon,
  CheckCircle as DoneIcon,
  Schedule as PendingIcon,
  Link as LinkIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
  Dashboard as DashboardIcon,
  Assessment as ReportIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import trelloService, {
  TrelloBoard,
  TrelloList,
  TrelloCard,
} from '../services/trello.service';
import { useAuthStore } from '../store/authStore';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const PRINTABILITY_USERS = ['lasanthaocl', 'sasiriocl', 'isuruocl', 'ashanocl'];

const CAD_BOARD_ID = '693fad64c4c2ff7fa1c925d2';
const BULK_BOARD_ID = '68d563573055f941475b6ba0';

const PrintingProcessManager: React.FC = () => {
  const { user } = useAuthStore();
  const location = useLocation();
  const canViewPrintabilityAnalytics = user?.username ? PRINTABILITY_USERS.includes(user.username.toLowerCase()) : false;

  const [boards, setBoards] = useState<TrelloBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<TrelloBoard | null>(null);
  const [lists, setLists] = useState<TrelloList[]>([]);
  const [cards, setCards] = useState<TrelloCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedCard, setSelectedCard] = useState<TrelloCard | null>(null);
  const [cardHistory, setCardHistory] = useState<any[]>([]);
  const [cardCreatedDate, setCardCreatedDate] = useState<string>('');
  const [cardCurrentList, setCardCurrentList] = useState<string>('');
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [rawDashboardData, setRawDashboardData] = useState<any>(null); // Store unfiltered data
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(false);
  const [dashboardProgress, setDashboardProgress] = useState<number>(0);
  const [dashboardMessage, setDashboardMessage] = useState<string>('');
  const [pdfGenerating, setPdfGenerating] = useState<boolean>(false);

  // Printability Analytics state
  const [printabilityData, setPrintabilityData] = useState<any>(null);
  const [printabilityLoading, setPrintabilityLoading] = useState(false);
  const [printabilityError, setPrintabilityError] = useState<string>('');
  const [snapshotDate, setSnapshotDate] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  });
  const [snapshotTime, setSnapshotTime] = useState<string>(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  });
  // Printability Trend state
  const [trendData, setTrendData] = useState<any>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string>('');
  const [trendStartDate, setTrendStartDate] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 13);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [trendEndDate, setTrendEndDate] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  });
  const [trendTime, setTrendTime] = useState<string>('23:59');
  const [cardTableOpen, setCardTableOpen] = useState(false);

  // Filter states for Bulk Dashboard
  const [filterRoutes, setFilterRoutes] = useState<string[]>([]); // ['RP', 'PSS', 'PD']
  const [filterPhases, setFilterPhases] = useState<string[]>([]); // ['Pre Production', 'Production', 'Post Production']
  const [filterLists, setFilterLists] = useState<string[]>([]); // Specific Trello list names
  const [filterPrintable, setFilterPrintable] = useState<string>('All'); // 'All', 'Printable', 'Non-Printable'
  const [filterDelayStatus, setFilterDelayStatus] = useState<string>('All'); // 'All', 'On Time', 'Delayed'
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filteredCards, setFilteredCards] = useState<any[]>([]); // Stores cards after filters are applied

  // ── CAD Manager state ──────────────────────────────────────
  const [managerSection, setManagerSection] = useState<'soff' | 'bulk' | 'cad'>(() => {
    if (location.pathname.endsWith('/bulk')) return 'bulk';
    if (location.pathname.endsWith('/cad')) return 'cad';
    return 'soff';
  });
  const [cadLists, setCadLists] = useState<TrelloList[]>([]);
  const [cadCards, setCadCards] = useState<TrelloCard[]>([]);
  const [cadLoading, setCadLoading] = useState<boolean>(false);
  const [cadError, setCadError] = useState<string>('');
  const [cadActiveTab, setCadActiveTab] = useState<number>(0);
  const [bulkActiveTab, setBulkActiveTab] = useState<number>(0);
  const [cadSearch, setCadSearch] = useState<string>('');
  const [cadSelectedCard, setCadSelectedCard] = useState<TrelloCard | null>(null);
  const [cadCardHistory, setCadCardHistory] = useState<any[]>([]);
  const [cadHistoryLoading, setCadHistoryLoading] = useState<boolean>(false);
  const [cadCardCreatedDate, setCadCardCreatedDate] = useState<string>('');
  const [cadCardCurrentList, setCadCardCurrentList] = useState<string>('');
  const [cadExportLoading, setCadExportLoading] = useState<boolean>(false);
  const [cadExportProgress, setCadExportProgress] = useState<number>(0);

  // Bulk board view state (separate from S/OFF lists/cards)
  const [bulkBoardLists, setBulkBoardLists] = useState<TrelloList[]>([]);
  const [bulkBoardCards, setBulkBoardCards] = useState<TrelloCard[]>([]);
  const [bulkBoardLoading, setBulkBoardLoading] = useState<boolean>(false);
  const [bulkExportLoading, setBulkExportLoading] = useState<boolean>(false);
  const [bulkExportProgress, setBulkExportProgress] = useState<number>(0);

  // Bulk lead time state
  const [bulkLeadTimeData, setBulkLeadTimeData] = useState<any>(null);
  const [bulkLeadTimeLoading, setBulkLeadTimeLoading] = useState<boolean>(false);
  const [bulkLeadTimeProgress, setBulkLeadTimeProgress] = useState<number>(0);
  const [bulkLeadTimeMessage, setBulkLeadTimeMessage] = useState<string>('');

  // Sync managerSection when route changes
  useEffect(() => {
    if (location.pathname.endsWith('/bulk')) {
      setManagerSection('bulk');
      // Auto-load bulk dashboard if not already loaded
      if (!dashboardStats || dashboardStats.boardType !== 'bulk') {
        fetchDashboardStats(BULK_BOARD_ID);
      }
    } else if (location.pathname.endsWith('/cad')) {
      setManagerSection('cad');
      if (cadCards.length === 0) fetchCadBoard();
    } else {
      setManagerSection('soff');
      // Auto-select the S/OFF board from already-loaded boards (or after fetchBoards completes)
      setSelectedBoard(prev => {
        if (prev) return prev; // already selected
        const soffBoard = boards.find(b => b.name.toLowerCase().includes('printing s/off'));
        return soffBoard || null;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    fetchBoards();
  }, []);

  useEffect(() => {
    if (selectedBoard) {
      if (activeTab === 0) {
        // Dashboard tab - only fetch if not already loaded
        if (!dashboardStats) {
          fetchDashboardStats(selectedBoard.id);
        }
      } else if (activeTab === 1) {
        fetchBoardData(selectedBoard.id);
      } else if (activeTab === 2 && canViewPrintabilityAnalytics) {
        fetchPrintabilityAnalytics();
      }
    }
  }, [selectedBoard, activeTab]);

  // Sync trend time whenever snapshot time changes so comparisons are consistent
  useEffect(() => {
    setTrendTime(snapshotTime);
  }, [snapshotTime]);

  // When snapshotDate changes, ensure the trend range always includes it.
  // Cards that were in the pipeline on snapshotDate may have moved out before
  // the trend's default start — expanding the range ensures they are captured.
  useEffect(() => {
    if (!snapshotDate) return;
    setTrendStartDate(prev => (snapshotDate < prev ? snapshotDate : prev));
    setTrendEndDate(prev => (snapshotDate > prev ? snapshotDate : prev));
  }, [snapshotDate]);

  const fetchPrintabilityAnalytics = async () => {
    if (!selectedBoard) return;
    setPrintabilityLoading(true);
    setPrintabilityError('');
    try {
      // Parse as local time, then convert to UTC ISO string so the server (UTC) gets the correct moment
      const localDt = new Date(`${snapshotDate}T${snapshotTime}:00`);
      const iso = localDt.toISOString();
      const data = await trelloService.getPrintabilityAnalytics(selectedBoard.id, iso);
      setPrintabilityData(data);
    } catch (err: any) {
      setPrintabilityError(err.message || 'Failed to load printability analytics');
    } finally {
      setPrintabilityLoading(false);
    }
  };

  const fetchPrintabilityTrend = async () => {
    if (!selectedBoard) return;
    setTrendLoading(true);
    setTrendError('');
    setTrendData(null);
    try {
      // Convert local trendTime to UTC equivalent so the server builds each day's snapshot at the correct moment
      const ref = new Date(`${trendStartDate}T${trendTime}:00`);
      const utcTimeOfDay = `${String(ref.getUTCHours()).padStart(2, '0')}:${String(ref.getUTCMinutes()).padStart(2, '0')}`;
      const data = await trelloService.getPrintabilityTrend(selectedBoard.id, trendStartDate, trendEndDate, utcTimeOfDay);
      setTrendData(data);
    } catch (err: any) {
      setTrendError(err.message || 'Failed to load printability trend');
    } finally {
      setTrendLoading(false);
    }
  };

  const fetchBoards = async () => {
    try {
      setLoading(true);
      setError('');
      const boardsData = await trelloService.getBoards();
      // Filter to show both "Printing S/OFF" and "Printing Bulk Management" boards
      // Support both "Managment" (typo) and "Management" (correct) spellings
      const filteredBoards = boardsData.filter((board) => {
        if (board.closed) return false;
        const boardName = board.name.toLowerCase();
        return (
          boardName.includes('printing s/off') || 
          boardName === 'printing bulk management'
        );
      });
      setBoards(filteredBoards);

      // Auto-select the S/OFF board when on the soff route or section (skip board picker)
      if (!location.pathname.endsWith('/bulk') && !location.pathname.endsWith('/cad')) {
        const soffBoard = filteredBoards.find(b => b.name.toLowerCase().includes('printing s/off'));
        if (soffBoard) {
          setSelectedBoard(soffBoard);
        }
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load Trello boards');
      setLoading(false);
    }
  };

  const fetchBoardData = async (boardId: string) => {
    try {
      setLoading(true);
      setError('');
      const [listsData, cardsData] = await Promise.all([
        trelloService.getLists(boardId),
        trelloService.getCards(boardId),
      ]);
      setLists(listsData.filter((list) => !list.closed));
      setCards(cardsData.filter((card) => !card.closed));
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load board data');
      setLoading(false);
    }
  };

  // ── CAD Board functions ──────────────────────────────────────
  const fetchCadBoard = async () => {
    try {
      setCadLoading(true);
      setCadError('');
      const [listsData, cardsData] = await Promise.all([
        trelloService.getLists(CAD_BOARD_ID),
        trelloService.getCards(CAD_BOARD_ID),
      ]);
      const visibleLists = listsData.filter((l) => !l.closed && l.name.trim().toUpperCase() !== 'CAD OUT');
      const visibleListIds = new Set(visibleLists.map((l) => l.id));
      setCadLists(visibleLists);
      setCadCards(cardsData.filter((c) => !c.closed && visibleListIds.has(c.idList)));
      setCadLoading(false);
    } catch (err: any) {
      setCadError(err.message || 'Failed to load CAD board data');
      setCadLoading(false);
    }
  };

  const handleCadCardClick = async (card: TrelloCard) => {
    setCadSelectedCard(card);
    setCadHistoryLoading(true);
    setCadCardCreatedDate('');
    setCadCardCurrentList('');
    setCadCardHistory([]);
    try {
      const actions = await trelloService.getCardActions(card.id);
      setCadCardHistory(actions);
      const createAction = actions.find((a: any) => a.type === 'createCard');
      if (createAction) {
        setCadCardCreatedDate(new Date(createAction.date).toLocaleString());
      }
      const listName = cadLists.find((l) => l.id === card.idList)?.name || 'Unknown';
      setCadCardCurrentList(listName);
    } catch {
      // history unavailable
    } finally {
      setCadHistoryLoading(false);
    }
  };

  const calculateCadTimeInLists = () => {
    if (!cadCardHistory.length) return [];
    const movements: Array<{ listName: string; date: Date }> = [];
    const createAction = cadCardHistory.find((a: any) => a.type === 'createCard');
    if (createAction?.data?.list) {
      movements.push({ listName: createAction.data.list.name, date: new Date(createAction.date) });
    }
    cadCardHistory
      .filter((a: any) => a.type === 'updateCard' && a.data?.listAfter)
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .forEach((a: any) => movements.push({ listName: a.data.listAfter.name, date: new Date(a.date) }));
    const now = new Date();
    const timeMap: Record<string, { totalMs: number; entries: number }> = {};
    movements.forEach((m, i) => {
      const endTime = i < movements.length - 1 ? movements[i + 1].date : now;
      const ms = endTime.getTime() - m.date.getTime();
      if (!timeMap[m.listName]) timeMap[m.listName] = { totalMs: 0, entries: 0 };
      timeMap[m.listName].totalMs += ms;
      timeMap[m.listName].entries += 1;
    });
    return Object.entries(timeMap).map(([listName, data]) => ({
      listName,
      days: Math.floor(data.totalMs / (1000 * 60 * 60 * 24)),
      hours: Math.floor((data.totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      entries: data.entries,
    }));
  };

  const handleCadExportToExcel = async () => {
    try {
      setCadExportLoading(true);
      setCadExportProgress(0);
      const result = await trelloService.exportBoardToExcel(CAD_BOARD_ID);
      const jobId = result.jobId;
      const interval = setInterval(async () => {
        try {
          const status = await trelloService.getExportStatus(jobId);
          setCadExportProgress(status.progress || 0);
          if (status.status === 'completed') {
            clearInterval(interval);
            const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
            const link = document.createElement('a');
            link.href = `${baseUrl}${status.downloadUrl}`;
            link.download = status.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setCadExportLoading(false);
          } else if (status.status === 'error') {
            clearInterval(interval);
            setCadError(status.error || 'Export failed');
            setCadExportLoading(false);
          }
        } catch {
          clearInterval(interval);
          setCadExportLoading(false);
        }
      }, 2000);
    } catch (err: any) {
      setCadError(err.message || 'Failed to export CAD board');
      setCadExportLoading(false);
    }
  };

  const getCadCardsForList = (listId: string) =>
    cadCards.filter((c) => c.idList === listId && (
      !cadSearch || c.name.toLowerCase().includes(cadSearch.toLowerCase())
    ));

  const getCadStats = () => {
    const now = new Date();
    return {
      total: cadCards.length,
      completed: cadCards.filter((c) => c.dueComplete).length,
      withDueDate: cadCards.filter((c) => c.due).length,
      overdue: cadCards.filter((c) => c.due && !c.dueComplete && new Date(c.due) < now).length,
    };
  };

  const fetchDashboardStats = async (boardId: string) => {
    try {
      setDashboardLoading(true);
      setDashboardProgress(0);
      setDashboardMessage('Starting dashboard generation...');
      setError('');
      
      console.log(`Starting dashboard generation for board: ${boardId}`);
      
      // Start dashboard job
      const result = await trelloService.getDashboardStats(boardId);
      const jobId = result.jobId;
      
      console.log('Dashboard job started:', jobId);
      
      // Poll for status
      const checkStatus = async () => {
        try {
          const status = await trelloService.getDashboardJobStatus(jobId);
          console.log('Dashboard status:', status);
          
          setDashboardProgress(status.progress || 0);
          setDashboardMessage(status.message || 'Processing...');
          
          if (status.status === 'completed') {
            setRawDashboardData(status.data); // Store raw unfiltered data
            setDashboardStats(status.data); // Initially show all data
            setDashboardLoading(false);
            setDashboardProgress(100);
            setDashboardMessage('');
            console.log('Dashboard generation completed successfully');
            // Reset filters when new data is loaded
            resetFilters();
          } else if (status.status === 'error') {
            setError(status.error || 'Dashboard generation failed');
            setDashboardLoading(false);
            setDashboardProgress(0);
            setDashboardMessage('');
          } else {
            // Still processing, check again in 2 seconds
            setTimeout(checkStatus, 2000);
          }
        } catch (err: any) {
          console.error('Status check error:', err);
          setError('Failed to check dashboard status');
          setDashboardLoading(false);
          setDashboardProgress(0);
          setDashboardMessage('');
        }
      };
      
      // Start polling
      setTimeout(checkStatus, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard statistics');
      setDashboardLoading(false);
      setDashboardProgress(0);
      setDashboardMessage('');
    }
  };

  const getCardsForList = (listId: string) => {
    return cards.filter((card) => card.idList === listId);
  };

  const handleCardClick = async (card: TrelloCard) => {
    setSelectedCard(card);
    setHistoryLoading(true);
    try {
      const actions = await trelloService.getCardActions(card.id);
      console.log('Card actions:', actions);
      setCardHistory(actions);
      
      // Find the create action to get the created date/time
      const createAction = actions.find((a: any) => a.type === 'createCard');
      if (createAction) {
        const createdDate = new Date(createAction.date);
        setCardCreatedDate(createdDate.toLocaleString());
      } else {
        setCardCreatedDate('Unknown');
      }
      
      // Get the current list name
      const currentList = lists.find((l) => l.id === card.idList);
      setCardCurrentList(currentList?.name || 'Unknown');
      
      setHistoryLoading(false);
    } catch (err: any) {
      console.error('Error loading card history:', err);
      setError(err.message || 'Failed to load card history');
      setHistoryLoading(false);
    }
  };

  const calculateTimeInLists = () => {
    if (!cardHistory || cardHistory.length === 0 || !selectedCard) {
      console.log('No history data:', { cardHistory, selectedCard });
      return [];
    }

    console.log('Calculating time for card:', selectedCard.name);
    console.log('Card history actions:', cardHistory);

    const listTimeMap: { [key: string]: { name: string; totalMs: number; entries: number } } = {};
    let currentTime = new Date();

    // Sort actions by date (oldest first)
    const sortedActions = [...cardHistory].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    console.log('Sorted actions:', sortedActions);

    // Find creation action
    const createAction = sortedActions.find((a) => a.type === 'createCard');
    if (!createAction) {
      console.log('No create action found');
      return [];
    }
    
    let previousTime = new Date(createAction.date);
    
    // Get the initial list from create action
    let trackingList = createAction.data?.list?.id || selectedCard.idList;
    let trackingListName = createAction.data?.list?.name || lists.find((l) => l.id === selectedCard.idList)?.name || 'Unknown';

    console.log('Starting tracking:', { trackingList, trackingListName, previousTime });

    // Process each list change action
    sortedActions.forEach((action, index) => {
      console.log(`Processing action ${index}:`, action);
      
      if (action.type === 'updateCard' && action.data?.listAfter) {
        const listBefore = action.data.listBefore;
        const listAfter = action.data.listAfter;
        const actionTime = new Date(action.date);

        console.log('List change detected:', { 
          from: listBefore?.name, 
          to: listAfter?.name, 
          time: actionTime 
        });

        // Calculate time in the list we're leaving
        const timeInList = actionTime.getTime() - previousTime.getTime();
        
        // Use trackingList (where the card was) not just listBefore
        const leavingListId = listBefore?.id || trackingList;
        const leavingListName = listBefore?.name || trackingListName;
        
        if (!listTimeMap[leavingListId]) {
          listTimeMap[leavingListId] = {
            name: leavingListName,
            totalMs: 0,
            entries: 0,
          };
        }
        listTimeMap[leavingListId].totalMs += timeInList;
        listTimeMap[leavingListId].entries += 1;
        
        console.log(`Added ${timeInList}ms to ${leavingListName}`);

        previousTime = actionTime;
        trackingList = listAfter.id;
        trackingListName = listAfter.name;
      }
    });

    // Add time for current list (where the card is now)
    const timeInCurrentList = currentTime.getTime() - previousTime.getTime();
    
    console.log('Current list calculation:', { 
      trackingList, 
      trackingListName, 
      timeInCurrentList
    });
    
    if (!listTimeMap[trackingList]) {
      listTimeMap[trackingList] = {
        name: trackingListName,
        totalMs: 0,
        entries: 0,
      };
    }
    listTimeMap[trackingList].totalMs += timeInCurrentList;
    listTimeMap[trackingList].entries += 1;
    
    console.log(`Added ${timeInCurrentList}ms to current list ${trackingListName}`);

    // Convert to array and calculate days
    const result = Object.entries(listTimeMap).map(([listId, data]) => ({
      listId,
      listName: data.name,
      days: Math.floor(data.totalMs / (1000 * 60 * 60 * 24)),
      hours: Math.floor((data.totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      entries: data.entries,
    }));

    console.log('Final result:', result);
    return result;
  };

  const handleExportToExcel = async () => {
    if (!selectedBoard || cards.length === 0) {
      setError('No data to export');
      return;
    }

    setExportLoading(true);
    setExportProgress(0);
    
    try {
      console.log(`Starting server-side export for board: ${selectedBoard.id}`);
      
      // Start export job
      const result = await trelloService.exportBoardToExcel(selectedBoard.id);
      const jobId = result.jobId;
      
      console.log('Export job started:', jobId);
      
      // Poll for status with retry on transient network errors
      let networkRetries = 0;
      const MAX_NETWORK_RETRIES = 8;
      const checkStatus = async () => {
        try {
          const status = await trelloService.getExportStatus(jobId);
          networkRetries = 0; // reset on success
          setExportProgress(status.progress || 0);
          if (status.status === 'completed') {
            const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
            const baseUrl = backendUrl.replace('/api', '');
            const downloadUrl = `${baseUrl}${status.downloadUrl}`;
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = status.filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setExportLoading(false);
            setExportProgress(0);
            alert(`Successfully exported ${status.totalCards} cards to Excel!`);
          } else if (status.status === 'error') {
            setError(status.error || 'Export failed');
            setExportLoading(false);
            setExportProgress(0);
          } else {
            setTimeout(checkStatus, 2000);
          }
        } catch (err: any) {
          const isNetworkError = err?.code === 'ERR_NETWORK' || err?.code === 'ERR_NETWORK_CHANGED' ||
            err?.message === 'Network Error' || (err?.code && String(err.code).startsWith('ERR_NETWORK'));
          const is404 = err?.response?.status === 404;
          if (is404) {
            setError('Export job was lost (server restarted). Please try the export again.');
            setExportLoading(false);
            setExportProgress(0);
          } else if (isNetworkError && networkRetries < MAX_NETWORK_RETRIES) {
            networkRetries++;
            // Retry after 3s on network blip
            setTimeout(checkStatus, 3000);
          } else {
            console.error('Status check error:', err);
            setError('Failed to check export status — please check your connection and try again.');
            setExportLoading(false);
            setExportProgress(0);
          }
        }
      };
      setTimeout(checkStatus, 2000);
      
    } catch (err: any) {
      console.error('Export error:', err);
      setError(err.message || 'Failed to export data to Excel');
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const fetchBulkBoardData = async () => {
    try {
      setBulkBoardLoading(true);
      setError('');
      const [listsData, cardsData] = await Promise.all([
        trelloService.getLists(BULK_BOARD_ID),
        trelloService.getCards(BULK_BOARD_ID),
      ]);
      setBulkBoardLists(listsData.filter((l) => !l.closed));
      setBulkBoardCards(cardsData.filter((c) => !c.closed));
      setBulkBoardLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load Bulk board data');
      setBulkBoardLoading(false);
    }
  };

  const fetchBulkLeadTime = async () => {
    try {
      setBulkLeadTimeLoading(true);
      setBulkLeadTimeProgress(0);
      setBulkLeadTimeMessage('Starting bulk lead time analysis...');
      setBulkLeadTimeData(null);
      setError('');
      const { jobId } = await trelloService.startBulkLeadTime(BULK_BOARD_ID);
      const checkStatus = async () => {
        try {
          const job = await trelloService.getBulkLeadTimeStatus(jobId);
          setBulkLeadTimeProgress(job.progress || 0);
          setBulkLeadTimeMessage(job.message || '');
          if (job.status === 'completed') {
            setBulkLeadTimeData(job.data);
            setBulkLeadTimeLoading(false);
          } else if (job.status === 'error') {
            setError(job.error || 'Lead time analysis failed');
            setBulkLeadTimeLoading(false);
          } else {
            setTimeout(checkStatus, 2000);
          }
        } catch (err: any) {
          setError(err.message || 'Failed to fetch lead time status');
          setBulkLeadTimeLoading(false);
        }
      };
      setTimeout(checkStatus, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to start lead time analysis');
      setBulkLeadTimeLoading(false);
    }
  };

  const handleBulkExportToExcel = async () => {
    if (bulkBoardCards.length === 0) {
      setError('No data to export');
      return;
    }
    setBulkExportLoading(true);
    setBulkExportProgress(0);
    try {
      const result = await trelloService.exportBoardToExcel(BULK_BOARD_ID);
      const jobId = result.jobId;
      let bulkNetworkRetries = 0;
      const MAX_BULK_RETRIES = 8;
      const checkStatus = async () => {
        try {
          const status = await trelloService.getExportStatus(jobId);
          bulkNetworkRetries = 0;
          setBulkExportProgress(status.progress || 0);
          if (status.status === 'completed') {
            const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
            const baseUrl = backendUrl.replace('/api', '');
            const downloadUrl = `${baseUrl}${status.downloadUrl}`;
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = status.filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setBulkExportLoading(false);
            setBulkExportProgress(0);
            alert(`Successfully exported ${status.totalCards} cards to Excel!`);
          } else if (status.status === 'error') {
            setError(status.error || 'Export failed');
            setBulkExportLoading(false);
            setBulkExportProgress(0);
          } else {
            setTimeout(checkStatus, 2000);
          }
        } catch (err: any) {
          const isNetworkError = err?.code === 'ERR_NETWORK' || err?.code === 'ERR_NETWORK_CHANGED' ||
            err?.message === 'Network Error' || (err?.code && String(err.code).startsWith('ERR_NETWORK'));
          const is404 = err?.response?.status === 404;
          if (is404) {
            setError('Export job was lost (server restarted). Please try the export again.');
            setBulkExportLoading(false);
            setBulkExportProgress(0);
          } else if (isNetworkError && bulkNetworkRetries < MAX_BULK_RETRIES) {
            bulkNetworkRetries++;
            setTimeout(checkStatus, 3000);
          } else {
            setError('Failed to check export status — please check your connection and try again.');
            setBulkExportLoading(false);
            setBulkExportProgress(0);
          }
        }
      };
      setTimeout(checkStatus, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to export Bulk data to Excel');
      setBulkExportLoading(false);
      setBulkExportProgress(0);
    }
  };

  const calculateTimeForCard = (card: TrelloCard, actions: any[]) => {
    console.log(`[calculateTimeForCard] Starting for card: ${card.name}`);
    console.log('[calculateTimeForCard] Card ID:', card.idList);
    console.log('[calculateTimeForCard] Actions count:', actions?.length || 0);
    
    const listTimeMap: { [key: string]: { name: string; totalMs: number; entries: number } } = {};
    let currentTime = new Date();

    if (!actions || actions.length === 0) {
      console.log('[calculateTimeForCard] No actions, using current list');
      // If no actions, card has been in current list since beginning
      const currentListObj = lists.find((l) => l.id === card.idList);
      if (currentListObj) {
        console.log('[calculateTimeForCard] Current list:', currentListObj.name);
        return [{
          listId: card.idList,
          listName: currentListObj.name,
          days: 0,
          hours: 0,
          entries: 1,
        }];
      }
      return [];
    }

    // Sort actions by date (oldest first)
    const sortedActions = [...actions].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    console.log('[calculateTimeForCard] Sorted actions:', sortedActions.length);

    // Find creation action
    const createAction = sortedActions.find((a) => a.type === 'createCard');
    console.log('[calculateTimeForCard] Create action found:', !!createAction);
    
    // If no create action, use earliest action or current time
    let previousTime: Date;
    let trackingList: string;
    let trackingListName: string;
    
    if (!createAction) {
      console.log('[calculateTimeForCard] No create action, looking for first update');
      // No create action found, start from first updateCard action or use current list
      const firstUpdate = sortedActions.find((a) => a.type === 'updateCard' && a.data?.listAfter);
      if (firstUpdate) {
        previousTime = new Date(firstUpdate.date);
        trackingList = firstUpdate.data.listBefore?.id || card.idList;
        trackingListName = firstUpdate.data.listBefore?.name || lists.find((l) => l.id === card.idList)?.name || 'Unknown';
        console.log('[calculateTimeForCard] Using first update, tracking list:', trackingListName);
      } else {
        console.log('[calculateTimeForCard] No useful actions, using current list');
        // No useful actions, use current list
        const currentListObj = lists.find((l) => l.id === card.idList);
        if (currentListObj) {
          return [{
            listId: card.idList,
            listName: currentListObj.name,
            days: 0,
            hours: 0,
            entries: 1,
          }];
        }
        return [];
      }
    } else {
      previousTime = new Date(createAction.date);
      trackingList = createAction.data?.list?.id || card.idList;
      trackingListName = createAction.data?.list?.name || lists.find((l) => l.id === card.idList)?.name || 'Unknown';
      console.log('[calculateTimeForCard] Starting from create action, list:', trackingListName);
    }

    // Process each list change action
    sortedActions.forEach((action) => {
      if (action.type === 'updateCard' && action.data?.listAfter) {
        const listBefore = action.data.listBefore;
        const listAfter = action.data.listAfter;
        const actionTime = new Date(action.date);

        // Calculate time in the list we're leaving
        const timeInList = actionTime.getTime() - previousTime.getTime();
        
        // Use trackingList (where the card was) not listBefore
        const leavingListId = listBefore?.id || trackingList;
        const leavingListName = listBefore?.name || trackingListName;
        
        if (!listTimeMap[leavingListId]) {
          listTimeMap[leavingListId] = {
            name: leavingListName,
            totalMs: 0,
            entries: 0,
          };
        }
        listTimeMap[leavingListId].totalMs += timeInList;
        listTimeMap[leavingListId].entries += 1;

        previousTime = actionTime;
        trackingList = listAfter.id;
        trackingListName = listAfter.name;
      }
    });

    // Add time for current list (where the card is now)
    const timeInCurrentList = currentTime.getTime() - previousTime.getTime();
    
    if (!listTimeMap[trackingList]) {
      listTimeMap[trackingList] = {
        name: trackingListName,
        totalMs: 0,
        entries: 0,
      };
    }
    listTimeMap[trackingList].totalMs += timeInCurrentList;
    listTimeMap[trackingList].entries += 1;

    // Convert to array and calculate days
    return Object.entries(listTimeMap).map(([listId, data]) => ({
      listId,
      listName: data.name,
      days: Math.floor(data.totalMs / (1000 * 60 * 60 * 24)),
      hours: Math.floor((data.totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      entries: data.entries,
    }));
  };

  const getCardStats = () => {
    const total = cards.length;
    const withDueDate = cards.filter((card) => card.due).length;
    const completed = cards.filter((card) => card.dueComplete).length;
    const overdue = cards.filter(
      (card) => card.due && !card.dueComplete && new Date(card.due) < new Date()
    ).length;

    return { total, withDueDate, completed, overdue };
  };

  const stats = selectedBoard ? getCardStats() : null;

  const bulkStats = bulkBoardCards.length > 0 ? {
    total: bulkBoardCards.length,
    withDueDate: bulkBoardCards.filter(c => c.due).length,
    completed: bulkBoardCards.filter(c => c.dueComplete).length,
    overdue: bulkBoardCards.filter(c => c.due && !c.dueComplete && new Date(c.due) < new Date()).length,
  } : null;

  const generatePDFReport = async () => {
    if (!dashboardStats || !selectedBoard) return;

    try {
      setPdfGenerating(true);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(0, 121, 191);
      pdf.text('Strike-Off Dashboard Report', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 10;
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Board: ${selectedBoard.name}`, pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 5;
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 15;

      // Overall Statistics
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Overall Statistics', 14, yPosition);
      yPosition += 10;

      const statsData = [
        ['Total Cards', dashboardStats.totalStrikeOffs.toString()],
        ['External Factors', dashboardStats.externalFactorsTotal.toString()],
        ['Internal Factors', dashboardStats.internalFactorsTotal.toString()],
        ['Digital Printing', dashboardStats.printingPathDistribution.digital.toString()],
        ['Rotary Printing', dashboardStats.printingPathDistribution.rotary.toString()],
      ];

      autoTable(pdf, {
        startY: yPosition,
        head: [['Metric', 'Value']],
        body: statsData,
        theme: 'grid',
        headStyles: { fillColor: [33, 150, 243] },
        margin: { left: 14, right: 14 },
      });

      yPosition = (pdf as any).lastAutoTable.finalY + 15;

      // Lead Time Distribution
      if (yPosition > pageHeight - 80) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.text('Lead Time Distribution', 14, yPosition);
      yPosition += 10;

      const leadTimeData = dashboardStats.leadTimeHistogram.map((item: any) => [
        item.range,
        item.count.toString(),
      ]);

      autoTable(pdf, {
        startY: yPosition,
        head: [['Lead Time Range (Days)', 'Number of Strike Offs']],
        body: leadTimeData,
        theme: 'striped',
        headStyles: { fillColor: [54, 162, 235] },
        margin: { left: 14, right: 14 },
      });

      yPosition = (pdf as any).lastAutoTable.finalY + 15;

      // External Factors
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.text('External Factors Breakdown', 14, yPosition);
      yPosition += 10;

      const externalData = dashboardStats.externalFactors.map((item: any) => [
        item.listName,
        item.cardCount.toString(),
      ]);

      autoTable(pdf, {
        startY: yPosition,
        head: [['External Factor', 'Strike Offs']],
        body: externalData,
        theme: 'striped',
        headStyles: { fillColor: [255, 99, 132] },
        margin: { left: 14, right: 14 },
      });

      yPosition = (pdf as any).lastAutoTable.finalY + 15;

      // Internal Factors
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.text('Internal Factors Breakdown', 14, yPosition);
      yPosition += 10;

      const internalData = dashboardStats.internalFactors.map((item: any) => [
        item.listName,
        item.cardCount.toString(),
      ]);

      autoTable(pdf, {
        startY: yPosition,
        head: [['Internal Stage', 'Strike Offs']],
        body: internalData,
        theme: 'striped',
        headStyles: { fillColor: [54, 162, 235] },
        margin: { left: 14, right: 14 },
      });

      // Cards Exceeding 5 Days
      if (dashboardStats.cardsExceeding5Days && dashboardStats.cardsExceeding5Days.length > 0) {
        yPosition = (pdf as any).lastAutoTable.finalY + 15;

        if (yPosition > pageHeight - 60) {
          pdf.addPage();
          yPosition = 20;
        }

        pdf.setFontSize(14);
        pdf.text(`Cards Exceeding 5 Days Lead Time (${dashboardStats.cardsExceeding5Days.length})`, 14, yPosition);
        yPosition += 10;

        const exceeding5DaysData = dashboardStats.cardsExceeding5Days.slice(0, 20).map((card: any) => [
          card.cardName,
          `${card.totalLeadTimeDays}d ${card.totalLeadTimeHours}h`,
          card.currentListName,
        ]);

        autoTable(pdf, {
          startY: yPosition,
          head: [['Card Name', 'Lead Time', 'Current List']],
          body: exceeding5DaysData,
          theme: 'striped',
          headStyles: { fillColor: [255, 87, 34] },
          margin: { left: 14, right: 14 },
        });

        if (dashboardStats.cardsExceeding5Days.length > 20) {
          yPosition = (pdf as any).lastAutoTable.finalY + 5;
          pdf.setFontSize(10);
          pdf.setTextColor(150, 150, 150);
          pdf.text(`Showing top 20 of ${dashboardStats.cardsExceeding5Days.length} cards`, 14, yPosition);
        }
      }

      // Save PDF
      const fileName = `Strike-Off-Dashboard-${selectedBoard.name.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF report');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Filter functions for Bulk Dashboard
  const resetFilters = () => {
    setFilterRoutes([]);
    setFilterPhases([]);
    setFilterLists([]);
    setFilterPrintable('All');
    setFilterDelayStatus('All');
  };

  const hasActiveFilters = () => {
    return filterRoutes.length > 0 || 
           filterPhases.length > 0 || 
           filterLists.length > 0 || 
           filterPrintable !== 'All' || 
           filterDelayStatus !== 'All';
  };

  // Apply filters and recalculate statistics (only for bulk dashboard)
  useEffect(() => {
    if (rawDashboardData && rawDashboardData.boardType === 'bulk') {
      applyFiltersAndRecalculate();
    }
  }, [filterRoutes, filterPhases, filterLists, filterPrintable, filterDelayStatus, rawDashboardData]);

  // This function filters cards and recalculates all dashboard statistics
  const applyFiltersAndRecalculate = () => {
    if (!rawDashboardData || rawDashboardData.boardType !== 'bulk') {
      return;
    }

    // If no filters are active, use raw data
    if (!hasActiveFilters()) {
      setDashboardStats(rawDashboardData);
      setFilteredCards(rawDashboardData.rawCards || []); // Show all cards when no filters
      return;
    }

    console.log('Applying filters to dashboard data...', {
      routes: filterRoutes,
      phases: filterPhases,
      lists: filterLists,
      printable: filterPrintable,
      delayStatus: filterDelayStatus
    });

    // Get raw cards from backend data
    const allCards = rawDashboardData.rawCards || [];
    
    if (allCards.length === 0) {
      console.warn('No raw card data available for filtering');
      setDashboardStats(rawDashboardData);
      return;
    }

    // Apply filters
    let filteredCards = allCards;
    
    // Create current date once for consistent delay status calculations
    const currentDate = new Date();

    // Filter by Routes (based on PRIMARY route label only)
    if (filterRoutes.length > 0) {
      const allRouteLabels = ['RP', 'PSS', 'PD', 'DAP', 'DRP', 'STP'];
      
      filteredCards = filteredCards.filter((card: any) => {
        if (!card.labels || card.labels.length === 0) return false;
        
        // Find the primary (first) route label
        const primaryRouteLabel = card.labels.find((label: any) => 
          label.name && allRouteLabels.includes(label.name.toUpperCase())
        );
        
        // Only include card if its primary route is in the filter
        return primaryRouteLabel && filterRoutes.includes(primaryRouteLabel.name);
      });
    }

    // Filter by Phases (based on list grouping)
    if (filterPhases.length > 0) {
      const wipGroups: { [key: string]: string[] } = {
        'Pre Production': ['printing office', 'wf pfp', 'w/f pfp', 'wfpfp', 'wf-pfp', 'wf mini bulk', 'w/f mini bulk', 'mini bulk', 'return to ppc', 'pfp return', 'paste', 'paste preparation', 'paste preperation'],
        'Production': ['color check & adjustment', 'color check and adjustment', 'color check', 'r1', 'r4', 'r5', 'd1', 'd2', 'sublimation'],
        'Post Production': ['steamer 1', 'steamer1', 'steamer 2', 'steamer2', 'stramer 1', 'stramer 2', 'washer']
      };

      filteredCards = filteredCards.filter((card: any) => {
        const listNameLower = (card.listName || '').toLowerCase().trim();
        return filterPhases.some((phase: string) => {
          const listNames = wipGroups[phase] || [];
          return listNames.some((name: string) => listNameLower.includes(name));
        });
      });
    }

    // Filter by Lists (exact list name)
    if (filterLists.length > 0) {
      filteredCards = filteredCards.filter((card: any) => 
        filterLists.includes(card.listName)
      );
    }

    // Filter by Printable status
    if (filterPrintable !== 'All') {
      const postProductionLists = ['steamer 1', 'steamer1', 'steamer 2', 'steamer2', 'stramer 1', 'stramer 2', 'washer'];
      const excludedPrintableLists = ['wf pfp', 'w/f pfp', 'wfpfp', 'wf-pfp', 'wf mini bulk', 'w/f mini bulk', 'mini bulk', 'return to ppc', 'pfp return'];
      const holdLists = ['hold'];
      
      filteredCards = filteredCards.filter((card: any) => {
        const listNameLower = (card.listName || '').toLowerCase().trim();
        const isAfterSteamer = postProductionLists.some((name: string) => listNameLower.includes(name));
        const isExcludedPrintable = excludedPrintableLists.some((name: string) => listNameLower.includes(name));
        const isHold = holdLists.some((name: string) => listNameLower.includes(name));
        const isPrintable = !isAfterSteamer && !isExcludedPrintable && !isHold;
        
        return filterPrintable === 'Printable' ? isPrintable : !isPrintable;
      });
    }

    // Filter by Delay Status
    if (filterDelayStatus !== 'All') {
      filteredCards = filteredCards.filter((card: any) => {
        if (!card.due) return false; // Exclude cards without due date
        
        const printingOutDate = new Date(card.due);
        const daysDifference = Math.ceil((printingOutDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
        const isOnTime = daysDifference >= 0;
        
        return filterDelayStatus === 'On Time' ? isOnTime : !isOnTime;
      });
    }

    console.log(`Filtered ${allCards.length} cards down to ${filteredCards.length} cards`);

    // Recalculate all statistics based on filtered cards
    const recalculateStats = () => {
      // Calculate total LBS
      const totalLBS = filteredCards.reduce((sum: number, card: any) => sum + card.lbsValue, 0);

      // Recalculate WIP stats
      const wipGroups: { [key: string]: string[] } = {
        'Pre Production': ['printing office', 'wf pfp', 'w/f pfp', 'wfpfp', 'wf-pfp', 'wf mini bulk', 'w/f mini bulk', 'mini bulk', 'return to ppc', 'pfp return', 'paste', 'paste preparation', 'paste preperation'],
        'Production': ['color check & adjustment', 'color check and adjustment', 'color check', 'r1', 'r4', 'r5', 'd1', 'd2', 'sublimation'],
        'Post Production': ['steamer 1', 'steamer1', 'steamer 2', 'steamer2', 'stramer 1', 'stramer 2', 'washer']
      };

      const wipStats: { [key: string]: number } = {};
      Object.entries(wipGroups).forEach(([phase, listNames]) => {
        const phaseCards = filteredCards.filter((card: any) => {
          const listNameLower = (card.listName || '').toLowerCase().trim();
          return listNames.some((name: string) => listNameLower.includes(name));
        });
        wipStats[phase] = phaseCards.reduce((sum: number, card: any) => sum + card.lbsValue, 0);
      });

      // Recalculate Post Production breakdown
      const postProductionBreakdown = {
        steamer1: 0,
        steamer2: 0,
        washer: 0
      };

      filteredCards.forEach((card: any) => {
        const listNameLower = (card.listName || '').toLowerCase().trim();
        if (listNameLower.includes('steamer 1') || listNameLower.includes('steamer1') || listNameLower.includes('stramer 1')) {
          postProductionBreakdown.steamer1 += card.lbsValue;
        } else if (listNameLower.includes('steamer 2') || listNameLower.includes('steamer2') || listNameLower.includes('stramer 2')) {
          postProductionBreakdown.steamer2 += card.lbsValue;
        } else if (listNameLower.includes('washer')) {
          postProductionBreakdown.washer += card.lbsValue;
        }
      });

      // Recalculate section-wise stats
      const sectionStatsMap: { [key: string]: any } = {};
      filteredCards.forEach((card: any) => {
        if (!sectionStatsMap[card.listName]) {
          sectionStatsMap[card.listName] = { cardCount: 0, totalLBS: 0, listId: card.listId };
        }
        sectionStatsMap[card.listName].cardCount++;
        sectionStatsMap[card.listName].totalLBS += card.lbsValue;
      });

      const sectionWiseStats = Object.entries(sectionStatsMap).map(([listName, data]: [string, any]) => ({
        listId: data.listId,
        listName: listName,
        cardCount: data.cardCount,
        totalLBS: data.totalLBS
      }));

      // Recalculate complete production by route
      const routeMap: { [key: string]: any } = {};
      filteredCards.forEach((card: any) => {
        card.labels?.forEach((label: any) => {
          const routeName = label.name;
          if (!routeMap[routeName]) {
            routeMap[routeName] = { cardCount: 0, lbsValue: 0 };
          }
          routeMap[routeName].cardCount++;
          routeMap[routeName].lbsValue += card.lbsValue;
        });
      });

      const completeProdByRoute = Object.entries(routeMap).map(([label, data]: [string, any]) => ({
        label: label,
        cardCount: data.cardCount,
        lbsValue: data.lbsValue
      }));

      // Recalculate rotary printers by route (only RP, PSS, PD)
      // Each card is counted only once using its primary (first) rotary route label to avoid double-counting
      const rotaryRoutes = ['RP', 'PSS', 'PD'];
      const rotaryPrintersByRoute = rotaryRoutes.map(route => {
        const routeCards = filteredCards.filter((card: any) => {
          // Get the primary (first) rotary route label for this card
          const primaryRouteLabel = card.labels?.find((label: any) => 
            rotaryRoutes.includes(label.name)
          );
          // Only count this card if its primary route label matches the current route
          return primaryRouteLabel && primaryRouteLabel.name === route;
        });
        return {
          label: route,
          cardCount: routeCards.length,
          lbsValue: routeCards.reduce((sum: number, card: any) => sum + card.lbsValue, 0)
        };
      }).filter(item => item.cardCount > 0);

      // Recalculate printable quantity (excluding post-production, return lists, WF PFP / mini bulk, and Hold)
      const postProductionLists = ['steamer 1', 'steamer1', 'steamer 2', 'steamer2', 'stramer 1', 'stramer 2', 'washer'];
      const excludedPrintableLists = ['wf pfp', 'w/f pfp', 'wfpfp', 'wf-pfp', 'wf mini bulk', 'w/f mini bulk', 'mini bulk', 'return to ppc', 'pfp return'];
      const holdLists = ['hold'];
      
      const printableCards = filteredCards.filter((card: any) => {
        const listNameLower = (card.listName || '').toLowerCase().trim();
        const isAfterSteamer = postProductionLists.some((name: string) => listNameLower.includes(name));
        const isExcludedPrintable = excludedPrintableLists.some((name: string) => listNameLower.includes(name));
        const isHold = holdLists.some((name: string) => listNameLower.includes(name));
        return !isAfterSteamer && !isExcludedPrintable && !isHold;
      });

      const printableQuantityLBS = printableCards.reduce((sum: number, card: any) => sum + card.lbsValue, 0);

      // Recalculate rotary printable by route
      // Each card is counted only once using its primary (first) rotary route label to avoid double-counting
      const rotaryPrintableByRoute = rotaryRoutes.map(route => {
        const routeCards = printableCards.filter((card: any) => {
          // Get the primary (first) rotary route label for this card
          const primaryRouteLabel = card.labels?.find((label: any) => 
            rotaryRoutes.includes(label.name)
          );
          // Only count this card if its primary route label matches the current route
          return primaryRouteLabel && primaryRouteLabel.name === route;
        });
        return {
          label: route,
          cardCount: routeCards.length,
          lbsValue: routeCards.reduce((sum: number, card: any) => sum + card.lbsValue, 0)
        };
      }).filter(item => item.cardCount > 0);

      // Recalculate digital printers by route (DAP, DRP, STP)
      const digitalRoutes = ['DAP', 'DRP', 'STP'];
      const digitalPrintersByRoute = digitalRoutes.map(route => {
        const routeCards = filteredCards.filter((card: any) => {
          const primaryRouteLabel = card.labels?.find((label: any) => 
            digitalRoutes.includes(label.name)
          );
          return primaryRouteLabel && primaryRouteLabel.name === route;
        });
        return {
          label: route,
          cardCount: routeCards.length,
          lbsValue: routeCards.reduce((sum: number, card: any) => sum + card.lbsValue, 0)
        };
      }).filter(item => item.cardCount > 0);

      // Recalculate digital printable by route
      const digitalPrintableByRoute = digitalRoutes.map(route => {
        const routeCards = printableCards.filter((card: any) => {
          const primaryRouteLabel = card.labels?.find((label: any) => 
            digitalRoutes.includes(label.name)
          );
          return primaryRouteLabel && primaryRouteLabel.name === route;
        });
        return {
          label: route,
          cardCount: routeCards.length,
          lbsValue: routeCards.reduce((sum: number, card: any) => sum + card.lbsValue, 0)
        };
      }).filter(item => item.cardCount > 0);

      // Recalculate delay status (using currentDate from outer scope)
      let onTimeLBS = 0;
      let delayedLBS = 0;
      let onTimeCount = 0;
      let delayedCount = 0;

      filteredCards.forEach((card: any) => {
        if (card.due) {
          const printingOutDate = new Date(card.due);
          const daysDifference = Math.ceil((printingOutDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysDifference >= 0) {
            onTimeLBS += card.lbsValue;
            onTimeCount++;
          } else {
            delayedLBS += card.lbsValue;
            delayedCount++;
          }
        }
      });

      const delayStatus = {
        onTime: { lbs: onTimeLBS, count: onTimeCount },
        delayed: { lbs: delayedLBS, count: delayedCount }
      };

      return {
        ...rawDashboardData,
        totalCards: filteredCards.length,
        totalLBS: totalLBS,
        printableQuantityLBS: printableQuantityLBS,
        wipStats: wipStats,
        postProductionBreakdown: postProductionBreakdown,
        sectionWiseStats: sectionWiseStats,
        completeProdByRoute: completeProdByRoute,
        rotaryPrintersByRoute: rotaryPrintersByRoute,
        rotaryPrintableByRoute: rotaryPrintableByRoute,
        digitalPrintersByRoute: digitalPrintersByRoute,
        digitalPrintableByRoute: digitalPrintableByRoute,
        delayStatus: delayStatus,
        _filtered: true,
        _filteredCardCount: filteredCards.length,
        _originalCardCount: allCards.length
      };
    };

    const filteredStats = recalculateStats();
    setDashboardStats(filteredStats);
    setFilteredCards(filteredCards); // Store filtered cards for display
    console.log('Filters applied and statistics recalculated:', filteredStats);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading && boards.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const renderBulkDashboard = () => {
    if (dashboardLoading) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography sx={{ mt: 3, mb: 2, fontSize: '1.1rem', fontWeight: 500 }}>
            {dashboardMessage || 'Loading bulk dashboard statistics...'}
          </Typography>
          <Box sx={{ width: '60%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dashboardProgress}%
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 8,
                bgcolor: '#e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${dashboardProgress}%`,
                  height: '100%',
                  bgcolor: '#1976d2',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    if (!dashboardStats) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Filter Panel for Bulk Dashboard */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: showFilters ? 2 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon sx={{ color: '#00695C' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#00695C' }}>
                Filters
              </Typography>
              {hasActiveFilters() && (
                <Chip 
                  label={`${[...filterRoutes, ...filterPhases, ...filterLists].length + (filterPrintable !== 'All' ? 1 : 0) + (filterDelayStatus !== 'All' ? 1 : 0)} active`}
                  size="small" 
                  color="primary" 
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hasActiveFilters() && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={resetFilters}
                  sx={{ color: '#d32f2f' }}
                >
                  Clear All
                </Button>
              )}
              <Button
                size="small"
                endIcon={<ExpandMoreIcon sx={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ color: '#00695C' }}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </Box>
          </Box>

          <Collapse in={showFilters}>
            <Grid container spacing={2}>
              {/* Routes Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Routes</InputLabel>
                  <Select
                    multiple
                    value={filterRoutes}
                    onChange={(e) => setFilterRoutes(e.target.value as string[])}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {['RP', 'PSS', 'PD', 'DAP', 'DRP', 'STP'].map((route) => (
                      <MenuItem key={route} value={route}>
                        <Checkbox checked={filterRoutes.indexOf(route) > -1} />
                        <ListItemText primary={route} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Phases Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Production Phase</InputLabel>
                  <Select
                    multiple
                    value={filterPhases}
                    onChange={(e) => setFilterPhases(e.target.value as string[])}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {['Pre Production', 'Production', 'Post Production'].map((phase) => (
                      <MenuItem key={phase} value={phase}>
                        <Checkbox checked={filterPhases.indexOf(phase) > -1} />
                        <ListItemText primary={phase} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Stage/List Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={dashboardStats.lists?.map((l: any) => l.name) || []}
                  value={filterLists}
                  onChange={(event, newValue) => setFilterLists(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Stage/List" placeholder="Select lists" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              </Grid>

              {/* Printable Status Filter */}
              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Printable</InputLabel>
                  <Select
                    value={filterPrintable}
                    onChange={(e) => setFilterPrintable(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Printable">Printable</MenuItem>
                    <MenuItem value="Non-Printable">Non-Printable</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Delay Status Filter */}
              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterDelayStatus}
                    onChange={(e) => setFilterDelayStatus(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="On Time">On Time</MenuItem>
                    <MenuItem value="Delayed">Delayed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Active Filters Display */}
            {hasActiveFilters() && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#00695C', mb: 1, display: 'block' }}>
                  Active Filters:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {filterRoutes.map((route) => (
                    <Chip 
                      key={route}
                      label={`Route: ${route}`}
                      size="small"
                      onDelete={() => setFilterRoutes(filterRoutes.filter(r => r !== route))}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                  {filterPhases.map((phase) => (
                    <Chip 
                      key={phase}
                      label={`Phase: ${phase}`}
                      size="small"
                      onDelete={() => setFilterPhases(filterPhases.filter(p => p !== phase))}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                  {filterLists.map((list) => (
                    <Chip 
                      key={list}
                      label={'List: ' + list}
                      size="small"
                      onDelete={() => setFilterLists(filterLists.filter(l => l !== list))}
                      color="info"
                      variant="outlined"
                    />
                  ))}
                  {filterPrintable !== 'All' && (
                    <Chip 
                      label={'Printable: ' + filterPrintable}
                      size="small"
                      onDelete={() => setFilterPrintable('All')}
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {filterDelayStatus !== 'All' && (
                    <Chip 
                      label={'Status: ' + filterDelayStatus}
                      size="small"
                      onDelete={() => setFilterDelayStatus('All')}
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            )}
          </Collapse>
        </Paper>

        {/* Overall Statistics */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportIcon /> Overall Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700 }}>
                  {((dashboardStats.wipStats['Pre Production'] || 0) + (dashboardStats.wipStats['Production'] || 0)).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  LBS Before Post Production
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Before steamers (Pre Production + Production)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                  {dashboardStats.printableQuantityLBS?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Printable Quantity (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Before steamers, excluding WF PFP
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                  {dashboardStats.totalLBS.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Quantity (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All active bulk orders
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Work in Progress (WIP) */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Work in Progress (WIP) - LBS by Phase
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Distribution of bulk quantity (LBS) across production phases
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 3, bgcolor: '#e3f2fd', borderRadius: 2, border: '2px solid #1976d2' }}>
                <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700 }}>
                  {dashboardStats.wipStats['Pre Production']?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                  Pre Production (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Printing Office, WF PFP, Mini Bulk, Return to PPC, Paste
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 3, bgcolor: '#fff3e0', borderRadius: 2, border: '2px solid #f57c00' }}>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                  {dashboardStats.wipStats['Production']?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                  Production (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Color Check, R1, R4, R5, D1, D2, Sublimation
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 3, bgcolor: '#e8f5e9', borderRadius: 2, border: '2px solid #2e7d32' }}>
                <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                  {dashboardStats.wipStats['Post Production']?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                  Post Production (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Steamer 1, Steamer 2, Washer
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Post Production WIP Breakdown */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Post Production WIP - Detailed Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Work in Progress for Post Production stages (Steamer 1, Steamer 2, Washer)
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 3, bgcolor: '#f3e5f5', borderRadius: 2, border: '2px solid #7b1fa2' }}>
                <Typography variant="h3" sx={{ color: '#7b1fa2', fontWeight: 700 }}>
                  {dashboardStats.postProductionBreakdown?.steamer1?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Steamer 1 (LBS)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 3, bgcolor: '#fce4ec', borderRadius: 2, border: '2px solid #c2185b' }}>
                <Typography variant="h3" sx={{ color: '#c2185b', fontWeight: 700 }}>
                  {dashboardStats.postProductionBreakdown?.steamer2?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Steamer 2 (LBS)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 3, bgcolor: '#e1f5fe', borderRadius: 2, border: '2px solid #0277bd' }}>
                <Typography variant="h3" sx={{ color: '#0277bd', fontWeight: 700 }}>
                  {dashboardStats.postProductionBreakdown?.washer?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Washer (LBS)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Rotary Printers Section (RP, PSS, PD) */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Rotary Printers (RP, PSS, PD)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#7b1fa2', fontWeight: 700 }}>
                  {(() => {
                    const rotaryRoutes = ['RP', 'PSS', 'PD'];
                    const preProdLists = ['printing office', 'wf pfp', 'w/f pfp', 'wfpfp', 'wf-pfp', 'wf mini bulk', 'w/f mini bulk', 'mini bulk', 'return to ppc', 'pfp return', 'paste', 'paste preparation', 'paste preperation'];
                    const prodLists = ['color check & adjustment', 'color check and adjustment', 'color check', 'r1', 'r4', 'r5', 'd1', 'd2', 'sublimation'];
                    return filteredCards
                      .filter((card: any) => card.labels?.some((lbl: any) => rotaryRoutes.includes(lbl.name)))
                      .filter((card: any) => {
                        const listLower = (card.listName || '').toLowerCase().trim();
                        return preProdLists.some(l => listLower.includes(l)) || prodLists.some(l => listLower.includes(l));
                      })
                      .reduce((sum: number, card: any) => sum + card.lbsValue, 0)
                      .toFixed(2);
                  })()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  LBS Before Post Production
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Before steamers (Pre Production + Production)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                  {((dashboardStats.rotaryPrintableByRoute || [])
                    .reduce((sum: number, item: any) => sum + item.lbsValue, 0)
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Printable Quantity (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Before steamers, excluding WF PFP
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                  {((dashboardStats.rotaryPrintersByRoute || [])
                    .reduce((sum: number, item: any) => sum + item.lbsValue, 0)
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Quantity (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All active bulk orders
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Digital Printers Section (DAP, DRP, STP) */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Digital Printers (DAP, DRP, STP)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700 }}>
                  {(() => {
                    const digitalRoutes = ['DAP', 'DRP', 'STP'];
                    const preProdLists = ['printing office', 'wf pfp', 'w/f pfp', 'wfpfp', 'wf-pfp', 'wf mini bulk', 'w/f mini bulk', 'mini bulk', 'return to ppc', 'pfp return', 'paste', 'paste preparation', 'paste preperation'];
                    const prodLists = ['color check & adjustment', 'color check and adjustment', 'color check', 'r1', 'r4', 'r5', 'd1', 'd2', 'sublimation'];
                    return filteredCards
                      .filter((card: any) => card.labels?.some((lbl: any) => digitalRoutes.includes(lbl.name)))
                      .filter((card: any) => {
                        const listLower = (card.listName || '').toLowerCase().trim();
                        return preProdLists.some(l => listLower.includes(l)) || prodLists.some(l => listLower.includes(l));
                      })
                      .reduce((sum: number, card: any) => sum + card.lbsValue, 0)
                      .toFixed(2);
                  })()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  LBS Before Post Production
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Before steamers (Pre Production + Production)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                  {((dashboardStats.digitalPrintableByRoute || [])
                    .reduce((sum: number, item: any) => sum + item.lbsValue, 0)
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Printable Quantity (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Before steamers, excluding WF PFP
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                  {((dashboardStats.digitalPrintersByRoute || [])
                    .reduce((sum: number, item: any) => sum + item.lbsValue, 0)
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Quantity (LBS)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All active bulk orders
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Routewise Overall Quantity */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Routewise Overall Quantity Breakdown (LBS)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Total bulk quantity distribution for all print routes (RP, PSS, PD, DAP, DRP, STP)
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Pie
              data={{
                labels: [...(dashboardStats.rotaryPrintersByRoute || []), ...(dashboardStats.digitalPrintersByRoute || [])].map((item: any) => item.label),
                datasets: [
                  {
                    label: 'LBS Value',
                    data: [...(dashboardStats.rotaryPrintersByRoute || []), ...(dashboardStats.digitalPrintersByRoute || [])].map((item: any) => item.lbsValue),
                    backgroundColor: [
                      'rgba(156, 39, 176, 0.6)',
                      'rgba(255, 152, 0, 0.6)',
                      'rgba(33, 150, 243, 0.6)',
                      'rgba(76, 175, 80, 0.6)',
                      'rgba(244, 67, 54, 0.6)',
                      'rgba(255, 193, 7, 0.6)',
                    ],
                    borderColor: [
                      'rgba(156, 39, 176, 1)',
                      'rgba(255, 152, 0, 1)',
                      'rgba(33, 150, 243, 1)',
                      'rgba(76, 175, 80, 1)',
                      'rgba(244, 67, 54, 1)',
                      'rgba(255, 193, 7, 1)',
                    ],
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                    labels: {
                      padding: 20,
                      font: {
                        size: 14,
                        weight: 'bold',
                      },
                    },
                  },
                  datalabels: {
                    display: true,
                    color: '#fff',
                    font: {
                      weight: 'bold',
                      size: 14,
                    },
                    formatter: (value: any, context: any) => {
                      const allRoutes = [...(dashboardStats.rotaryPrintersByRoute || []), ...(dashboardStats.digitalPrintersByRoute || [])];
                      const total = allRoutes.reduce((sum: number, item: any) => sum + item.lbsValue, 0);
                      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                      return `${value.toFixed(0)} LBS\n${percentage}%`;
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed || 0;
                        const allRoutes = [...(dashboardStats.rotaryPrintersByRoute || []), ...(dashboardStats.digitalPrintersByRoute || [])];
                        const total = allRoutes.reduce((sum: number, item: any) => sum + item.lbsValue, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        const item = allRoutes[context.dataIndex];
                        return `${context.label}: ${value.toFixed(2)} LBS (${percentage}%) - ${item.cardCount} batches`;
                      }
                    }
                  }
                },
              }}
            />
          </Box>
        </Paper>

        {/* Routewise Printable Quantity */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Routewise Printable Quantity Breakdown (LBS)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Printable quantity for all routes (before steamers, excluding WF PFP)
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Pie
              data={{
                labels: [...(dashboardStats.rotaryPrintableByRoute || []), ...(dashboardStats.digitalPrintableByRoute || [])].map((item: any) => item.label),
                datasets: [
                  {
                    label: 'Printable LBS',
                    data: [...(dashboardStats.rotaryPrintableByRoute || []), ...(dashboardStats.digitalPrintableByRoute || [])].map((item: any) => item.lbsValue),
                    backgroundColor: [
                      'rgba(76, 175, 80, 0.6)',
                      'rgba(255, 193, 7, 0.6)',
                      'rgba(3, 169, 244, 0.6)',
                      'rgba(156, 39, 176, 0.6)',
                      'rgba(244, 67, 54, 0.6)',
                      'rgba(255, 152, 0, 0.6)',
                    ],
                    borderColor: [
                      'rgba(76, 175, 80, 1)',
                      'rgba(255, 193, 7, 1)',
                      'rgba(3, 169, 244, 1)',
                      'rgba(156, 39, 176, 1)',
                      'rgba(244, 67, 54, 1)',
                      'rgba(255, 152, 0, 1)',
                    ],
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                    labels: {
                      padding: 20,
                      font: {
                        size: 14,
                        weight: 'bold',
                      },
                    },
                  },
                  datalabels: {
                    display: true,
                    color: '#fff',
                    font: {
                      weight: 'bold',
                      size: 14,
                    },
                    formatter: (value: any, context: any) => {
                      const allRoutes = [...(dashboardStats.rotaryPrintableByRoute || []), ...(dashboardStats.digitalPrintableByRoute || [])];
                      const total = allRoutes.reduce((sum: number, item: any) => sum + item.lbsValue, 0);
                      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                      return `${value.toFixed(0)} LBS\n${percentage}%`;
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed || 0;
                        const allRoutes = [...(dashboardStats.rotaryPrintableByRoute || []), ...(dashboardStats.digitalPrintableByRoute || [])];
                        const total = allRoutes.reduce((sum: number, item: any) => sum + item.lbsValue, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        const item = allRoutes[context.dataIndex];
                        return `${context.label}: ${value.toFixed(2)} LBS (${percentage}%) - ${item.cardCount} batches`;
                      }
                    }
                  }
                },
              }}
            />
          </Box>
        </Paper>

        {/* Delay Status */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Delay Status - On Time vs Delayed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Batch status based on Printing Out Date compared to current date
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Pie
                  data={{
                    labels: ['On Time', 'Delayed'],
                    datasets: [
                      {
                        label: 'LBS Value',
                        data: [
                          dashboardStats.delayStatus?.onTime?.lbs || 0,
                          dashboardStats.delayStatus?.delayed?.lbs || 0
                        ],
                        backgroundColor: [
                          'rgba(76, 175, 80, 0.6)',
                          'rgba(244, 67, 54, 0.6)',
                        ],
                        borderColor: [
                          'rgba(76, 175, 80, 1)',
                          'rgba(244, 67, 54, 1)',
                        ],
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom' as const,
                        labels: {
                          padding: 20,
                          font: {
                            size: 14,
                            weight: 'bold',
                          },
                        },
                      },
                      datalabels: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed || 0;
                            const total = (dashboardStats.delayStatus?.onTime?.lbs || 0) + (dashboardStats.delayStatus?.delayed?.lbs || 0);
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            const count = context.dataIndex === 0 ? dashboardStats.delayStatus?.onTime?.count : dashboardStats.delayStatus?.delayed?.count;
                            return `${context.label}: ${value.toFixed(2)} LBS (${percentage}%) - ${count} batches`;
                          }
                        }
                      }
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box textAlign="center" sx={{ p: 3, bgcolor: '#e8f5e9', borderRadius: 2, border: '2px solid #4caf50' }}>
                    <Typography variant="h3" sx={{ color: '#4caf50', fontWeight: 700 }}>
                      {dashboardStats.delayStatus?.onTime?.count || 0}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                      On Time Batches
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 600 }}>
                      {dashboardStats.delayStatus?.onTime?.lbs?.toFixed(2) || '0.00'} LBS
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Printing Out Date ≥ Current Date
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box textAlign="center" sx={{ p: 3, bgcolor: '#ffebee', borderRadius: 2, border: '2px solid #f44336' }}>
                    <Typography variant="h3" sx={{ color: '#f44336', fontWeight: 700 }}>
                      {dashboardStats.delayStatus?.delayed?.count || 0}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                      Delayed Batches
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 600 }}>
                      {dashboardStats.delayStatus?.delayed?.lbs?.toFixed(2) || '0.00'} LBS
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Printing Out Date &lt; Current Date
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Complete Production by Print Route */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Complete Production by Print Route (LBS)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Distribution of bulk quantity across different print routes (RP, PSS, PD, DRP, DAP, STP)
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Pie
              data={{
                labels: dashboardStats.completeProdByRoute.map((item: any) => item.label),
                datasets: [
                  {
                    label: 'LBS Value',
                    data: dashboardStats.completeProdByRoute.map((item: any) => item.lbsValue),
                    backgroundColor: [
                      'rgba(255, 99, 132, 0.6)',
                      'rgba(54, 162, 235, 0.6)',
                      'rgba(255, 206, 86, 0.6)',
                      'rgba(75, 192, 192, 0.6)',
                      'rgba(153, 102, 255, 0.6)',
                      'rgba(255, 159, 64, 0.6)',
                    ],
                    borderColor: [
                      'rgba(255, 99, 132, 1)',
                      'rgba(54, 162, 235, 1)',
                      'rgba(255, 206, 86, 1)',
                      'rgba(75, 192, 192, 1)',
                      'rgba(153, 102, 255, 1)',
                      'rgba(255, 159, 64, 1)',
                    ],
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'right' as const,
                    labels: {
                      padding: 15,
                      font: {
                        size: 13,
                      },
                    },
                  },
                  datalabels: {
                    display: true,
                    color: '#fff',
                    font: {
                      weight: 'bold',
                      size: 13,
                    },
                    formatter: (value: any, context: any) => {
                      const total = dashboardStats.totalLBS;
                      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                      return `${value.toFixed(0)} LBS\n${percentage}%`;
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed || 0;
                        const total = dashboardStats.totalLBS;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        const item = dashboardStats.completeProdByRoute[context.dataIndex];
                        return `${context.label}: ${value.toFixed(2)} LBS (${percentage}%) - ${item.cardCount} cards`;
                      }
                    }
                  }
                },
              }}
            />
          </Box>
        </Paper>

        {/* Section-wise Statistics */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> LBS by Process Stage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Bulk quantity (LBS) distribution across all process stages
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar
              data={{
                labels: dashboardStats.sectionWiseStats.map((item: any) => item.listName),
                datasets: [
                  {
                    label: 'Total LBS',
                    data: dashboardStats.sectionWiseStats.map((item: any) => item.totalLBS),
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                  padding: {
                    top: 40,
                  },
                },
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: false,
                  },
                  datalabels: {
                    anchor: 'end',
                    align: 'top',
                    offset: 8,
                    formatter: (value: any) => Math.round(value),
                    rotation: -90,
                    font: {
                      weight: 'bold',
                      size: 15,
                    },
                    color: '#000',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y || 0;
                        const item = dashboardStats.sectionWiseStats[context.dataIndex];
                        return `${Math.round(value)} LBS (${item.cardCount} cards)`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Process Stage',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                  y: {
                    beginAtZero: true,
                    grace: '15%',
                    title: {
                      display: true,
                      text: 'Quantity (LBS)',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </Paper>

        {/* Card Details Table */}
        {filteredCards.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DashboardIcon /> Card Details {hasActiveFilters() && `(${filteredCards.length} cards)`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {hasActiveFilters() ? 'Showing filtered card details' : 'Showing all cards'}
            </Typography>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Card Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>List/Stage</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Routes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'right' }}>LBS Value</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCards.map((card: any, index: number) => {
                    const dueDate = card.due ? new Date(card.due) : null;
                    const currentDate = new Date();
                    const isDelayed = dueDate && dueDate < currentDate;
                    const daysDifference = dueDate ? Math.ceil((dueDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                    
                    return (
                      <TableRow key={card.id || index} hover>
                        <TableCell>{card.name || 'Unnamed Card'}</TableCell>
                        <TableCell>{card.listName || 'Unknown'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {card.labels && card.labels.length > 0 ? (
                              card.labels.map((label: any, idx: number) => (
                                <Chip 
                                  key={idx}
                                  label={label.name}
                                  size="small"
                                  sx={{ 
                                    bgcolor: label.color || '#ccc',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">No route</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                          {card.lbsValue ? card.lbsValue.toFixed(2) : '0.00'}
                        </TableCell>
                        <TableCell>
                          {dueDate ? (
                            <Typography variant="body2" sx={{ color: isDelayed ? '#d32f2f' : '#2e7d32' }}>
                              {dueDate.toLocaleDateString()}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">No due date</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {dueDate ? (
                            <Chip
                              label={isDelayed ? `Delayed (${Math.abs(daysDifference || 0)}d)` : `On Time (+${daysDifference}d)`}
                              size="small"
                              sx={{
                                bgcolor: isDelayed ? '#ffebee' : '#e8f5e9',
                                color: isDelayed ? '#d32f2f' : '#2e7d32',
                                fontWeight: 600
                              }}
                            />
                          ) : (
                            <Chip label="No Date" size="small" sx={{ bgcolor: '#f5f5f5' }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {card.shortUrl ? (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.open(card.shortUrl, '_blank')}
                              title="Open in Trello"
                            >
                              <LinkIcon />
                            </IconButton>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Total: {filteredCards.length} cards | Total LBS: {filteredCards.reduce((sum: number, card: any) => sum + (card.lbsValue || 0), 0).toFixed(2)}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  const renderBulkLeadTimeDashboard = () => {
    if (bulkLeadTimeLoading) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography sx={{ mt: 3, mb: 2, fontSize: '1.1rem', fontWeight: 500 }}>
            {bulkLeadTimeMessage || 'Analyzing lead times...'}
          </Typography>
          <Box sx={{ width: '60%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Progress</Typography>
              <Typography variant="caption" color="text.secondary">{bulkLeadTimeProgress}%</Typography>
            </Box>
            <Box sx={{ width: '100%', height: 8, bgcolor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ width: `${bulkLeadTimeProgress}%`, height: '100%', bgcolor: '#00695C', transition: 'width 0.3s ease' }} />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            Fetching card history — this may take a minute for large boards.
          </Typography>
        </Box>
      );
    }

    if (!bulkLeadTimeData) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={4}>
          <Typography color="text.secondary">
            Click below to run the lead time analysis. This fetches card history from Trello and may take a minute.
          </Typography>
          <Button variant="contained" onClick={fetchBulkLeadTime} sx={{ bgcolor: '#00695C', '&:hover': { bgcolor: '#004D40' } }}>
            Load Lead Time Analysis
          </Button>
        </Box>
      );
    }

    const ltd = bulkLeadTimeData;
    const avgMs = ltd.highestLeadTimeCards && ltd.highestLeadTimeCards.length > 0
      ? ltd.highestLeadTimeCards.reduce((s: number, c: any) => s + c.totalLeadTimeMs, 0) / ltd.totalCards
      : 0;
    const avgDays = Math.floor(avgMs / 86400000);
    const avgHours = Math.floor((avgMs % 86400000) / 3600000);

    return (
      <Box>
        {/* Summary Stats */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon /> Lead Time Overview
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                  {avgDays}d {avgHours}h
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Average Lead Time
                </Typography>
                <Typography variant="caption" color="text.secondary">Across all active bulk cards</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                  {ltd.cardsExceeding10Days ? ltd.cardsExceeding10Days.length : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Cards Exceeding 10 Days
                </Typography>
                <Typography variant="caption" color="text.secondary">Require attention</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700 }}>
                  {ltd.totalCards}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Active Bulks
                </Typography>
                <Typography variant="caption" color="text.secondary">In active lists</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Lead Time Histogram */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Lead Time Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Distribution of bulk orders by total lead time spent in active processing stages
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar
              data={{
                labels: ltd.leadTimeHistogram.map((item: any) => item.range),
                datasets: [{
                  label: 'Number of Bulk Orders',
                  data: ltd.leadTimeHistogram.map((item: any) => item.count),
                  backgroundColor: 'rgba(0, 105, 92, 0.6)',
                  borderColor: 'rgba(0, 105, 92, 1)',
                  borderWidth: 1,
                }],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top' as const },
                  datalabels: {
                    anchor: 'end', align: 'top',
                    formatter: (value: any) => value,
                    font: { weight: 'bold', size: 12 },
                    color: '#000',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y || 0;
                        const total = ltd.totalCards;
                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${value} bulk orders (${pct}%)`;
                      }
                    }
                  }
                },
                scales: {
                  x: { title: { display: true, text: 'Lead Time Range (Days)', font: { weight: 'bold', size: 14 } } },
                  y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Number of Bulk Orders', font: { weight: 'bold', size: 14 } } },
                },
              }}
            />
          </Box>
        </Paper>

        {/* ── PI Date (Due Date) Status ─────────────────────────────────── */}
        {ltd.dueDateStats && (
          <>
            {/* Summary row */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon /> PI Date (Product Invoice Date) Status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Statistical overview of bulk orders relative to their PI due dates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box textAlign="center" sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 2, border: '2px solid #ef9a9a' }}>
                    <Typography variant="h3" sx={{ color: '#c62828', fontWeight: 700 }}>
                      {ltd.dueDateStats.overdueCount}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828' }}>Overdue</Typography>
                    <Typography variant="caption" color="text.secondary">Past PI date, not done</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box textAlign="center" sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '2px solid #a5d6a7' }}>
                    <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                      {ltd.dueDateStats.onTimeCount}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>On Track</Typography>
                    <Typography variant="caption" color="text.secondary">PI date not yet reached</Typography>
                  </Box>
                </Grid>

              </Grid>
            </Paper>

            {/* Overdue + Upcoming distribution side by side */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#c62828' }}>
                    Overdue Distribution
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    How many days past the PI date
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <Bar
                      data={{
                        labels: ltd.dueDateStats.overdueDistribution.map((d: any) => d.range),
                        datasets: [{
                          label: 'Overdue Bulk Orders',
                          data: ltd.dueDateStats.overdueDistribution.map((d: any) => d.count),
                          backgroundColor: 'rgba(198, 40, 40, 0.6)',
                          borderColor: 'rgba(198, 40, 40, 1)',
                          borderWidth: 1,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          datalabels: {
                            anchor: 'end', align: 'top',
                            formatter: (v: any) => v || '',
                            font: { weight: 'bold', size: 12 },
                            color: '#c62828',
                          },
                        },
                        scales: {
                          x: { title: { display: true, text: 'Days Overdue', font: { weight: 'bold' } } },
                          y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Cards', font: { weight: 'bold' } } },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
                    Upcoming PI Dates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Days remaining until the PI date
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <Bar
                      data={{
                        labels: ltd.dueDateStats.upcomingDistribution.map((d: any) => d.range),
                        datasets: [{
                          label: 'On-Track Bulk Orders',
                          data: ltd.dueDateStats.upcomingDistribution.map((d: any) => d.count),
                          backgroundColor: 'rgba(46, 125, 50, 0.6)',
                          borderColor: 'rgba(46, 125, 50, 1)',
                          borderWidth: 1,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          datalabels: {
                            anchor: 'end', align: 'top',
                            formatter: (v: any) => v || '',
                            font: { weight: 'bold', size: 12 },
                            color: '#2e7d32',
                          },
                        },
                        scales: {
                          x: { title: { display: true, text: 'Days Remaining', font: { weight: 'bold' } } },
                          y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Cards', font: { weight: 'bold' } } },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Due Soon alert table (within 7 days) */}
            {ltd.dueDateStats.dueSoonCards && ltd.dueDateStats.dueSoonCards.length > 0 && (
              <Paper sx={{ p: 3, mb: 3, border: '1px solid #ffe082' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#f57f17', display: 'flex', alignItems: 'center', gap: 1 }}>
                  ⚠️ Due Within 7 Days ({ltd.dueDateStats.dueSoonCards.length} cards)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fff8e1' }}>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Card Name</strong></TableCell>
                        <TableCell align="center"><strong>PI Date</strong></TableCell>
                        <TableCell align="center"><strong>Days Remaining</strong></TableCell>
                        <TableCell><strong>Current Section</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ltd.dueDateStats.dueSoonCards.map((card: any, i: number) => (
                        <TableRow key={card.cardId} sx={{ bgcolor: card.daysRemaining <= 2 ? '#fff3e0' : 'inherit' }}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{card.cardName}</Typography></TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{new Date(card.dueDate).toLocaleDateString()}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={card.daysRemaining === 0 ? 'Today' : `${card.daysRemaining}d`}
                              color={card.daysRemaining <= 2 ? 'warning' : 'success'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell><Chip label={card.listName} variant="outlined" size="small" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Overdue cards table */}
            {ltd.dueDateStats.overdueCards && ltd.dueDateStats.overdueCards.length > 0 && (
              <Paper sx={{ p: 3, mb: 3, border: '1px solid #ef9a9a' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#c62828', display: 'flex', alignItems: 'center', gap: 1 }}>
                  🔴 Overdue Bulk Orders ({ltd.dueDateStats.overdueCards.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Sorted by most overdue first
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Card Name</strong></TableCell>
                        <TableCell align="center"><strong>PI Date</strong></TableCell>
                        <TableCell align="center"><strong>Days Overdue</strong></TableCell>
                        <TableCell><strong>Current Section</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ltd.dueDateStats.overdueCards.map((card: any, i: number) => (
                        <TableRow
                          key={card.cardId}
                          sx={{ bgcolor: card.daysOverdue >= 20 ? '#ffebee' : card.daysOverdue >= 10 ? '#fff3e0' : 'inherit' }}
                        >
                          <TableCell>{i + 1}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{card.cardName}</Typography></TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{new Date(card.dueDate).toLocaleDateString()}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${card.daysOverdue}d late`}
                              color={card.daysOverdue >= 20 ? 'error' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell><Chip label={card.listName} variant="outlined" size="small" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
        {/* ─────────────────────────────────────────────────────────────── */}

        {/* Cards per Section bar chart */}
        {ltd.sectionLeadTimeStats && ltd.sectionLeadTimeStats.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DashboardIcon /> Bulk Orders by Section
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Number of active bulk orders currently in each processing section
            </Typography>
            <Box sx={{ height: 400 }}>
              <Bar
                data={{
                  labels: ltd.sectionLeadTimeStats.map((s: any) => s.listName),
                  datasets: [{
                    label: 'Number of Bulk Orders',
                    data: ltd.sectionLeadTimeStats.map((s: any) => s.cardCount),
                    backgroundColor: 'rgba(0, 105, 92, 0.6)',
                    borderColor: 'rgba(0, 105, 92, 1)',
                    borderWidth: 1,
                  }],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' as const },
                    datalabels: {
                      anchor: 'end', align: 'top',
                      formatter: (value: any) => value,
                      font: { weight: 'bold', size: 12 },
                      color: '#000',
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return `${context.parsed.y} bulk order(s)`;
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      title: { display: true, text: 'Section', font: { weight: 'bold', size: 14 } },
                      ticks: { maxRotation: 45, minRotation: 20 },
                    },
                    y: {
                      beginAtZero: true,
                      ticks: { stepSize: 1 },
                      title: { display: true, text: 'Number of Bulk Orders', font: { weight: 'bold', size: 14 } },
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        )}

        {/* Section-wise average lead time */}
        {ltd.sectionLeadTimeStats && ltd.sectionLeadTimeStats.filter((s: any) => s.cardCount > 0).length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DashboardIcon /> Average Lead Time per Section
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Average total lead time of bulk orders currently in each processing section
            </Typography>
            <Box sx={{ height: 400 }}>
              <Bar
                data={{
                  labels: ltd.sectionLeadTimeStats.filter((s: any) => s.cardCount > 0).map((s: any) => s.listName),
                  datasets: [{
                    label: 'Avg Lead Time (Days)',
                    data: ltd.sectionLeadTimeStats.filter((s: any) => s.cardCount > 0).map((s: any) => parseFloat((s.avgLeadTimeDays + s.avgLeadTimeHours / 24).toFixed(1))),
                    backgroundColor: 'rgba(25, 118, 210, 0.6)',
                    borderColor: 'rgba(25, 118, 210, 1)',
                    borderWidth: 1,
                  }],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: 'y' as const,
                  plugins: {
                    legend: { position: 'top' as const },
                    datalabels: {
                      anchor: 'end', align: 'right',
                      formatter: (value: any) => `${value}d`,
                      font: { weight: 'bold', size: 11 },
                      color: '#000',
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const s = ltd.sectionLeadTimeStats.filter((s: any) => s.cardCount > 0)[context.dataIndex];
                          return `Avg: ${s.avgLeadTimeDays}d ${s.avgLeadTimeHours}h (${s.cardCount} cards)`;
                        }
                      }
                    }
                  },
                  scales: {
                    x: { beginAtZero: true, title: { display: true, text: 'Average Lead Time (Days)', font: { weight: 'bold', size: 13 } } },
                    y: { ticks: { font: { size: 11 } } },
                  },
                }}
              />
            </Box>
          </Paper>
        )}

        {/* Cards Exceeding 10 Days */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon /> Bulk Orders Exceeding 10 Days Lead Time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All bulk orders with total lead time exceeding 10 days, sorted by highest lead time first
          </Typography>
          {ltd.cardsExceeding10Days && ltd.cardsExceeding10Days.length > 0 ? (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00' }}>
                  ⚠️ {ltd.cardsExceeding10Days.length} bulk order(s) exceeding 10 days lead time
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Card Name</strong></TableCell>
                      <TableCell align="center"><strong>Total Lead Time</strong></TableCell>
                      <TableCell><strong>Current List</strong></TableCell>
                      <TableCell><strong>Longest Stage</strong></TableCell>
                      <TableCell align="center"><strong>Time in Longest Stage</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ltd.cardsExceeding10Days.map((card: any, index: number) => (
                      <TableRow
                        key={card.cardId}
                        sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' }, bgcolor: card.totalLeadTimeDays >= 20 ? '#ffebee' : '#fff3e0' }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{card.cardName}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${card.totalLeadTimeDays}d ${card.totalLeadTimeHours}h`}
                            color={card.totalLeadTimeDays >= 20 ? 'error' : 'warning'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={card.currentListName} variant="outlined" size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{card.maxTimeListName}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {card.maxTimeDays}d {card.maxTimeHours}h
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e9', borderRadius: 2 }}>
              <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                ✅ No bulk orders exceeding 10 days lead time
              </Typography>
            </Box>
          )}
        </Paper>


      </Box>
    );
  };

  const renderDashboard = () => {
    if (dashboardLoading) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography sx={{ mt: 3, mb: 2, fontSize: '1.1rem', fontWeight: 500 }}>
            {dashboardMessage || 'Loading dashboard statistics...'}
          </Typography>
          <Box sx={{ width: '60%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dashboardProgress}%
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 8,
                bgcolor: '#e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${dashboardProgress}%`,
                  height: '100%',
                  bgcolor: '#1976d2',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    if (!dashboardStats) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Overall Statistics */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportIcon /> Overall Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700 }}>
                  {dashboardStats.totalStrikeOffs}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Cards
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active Strike Offs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#d32f2f', fontWeight: 700 }}>
                  {dashboardStats.externalFactorsTotal}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  External Factors
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Waiting on external parties
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700 }}>
                  {dashboardStats.internalFactorsTotal}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Internal Factors
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Internal processing
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#7b1fa2', fontWeight: 700 }}>
                  {dashboardStats.printingPathDistribution.digital}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Digital Printing
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PZ, PT, PGZ, PRE prefixes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box textAlign="center" sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                  {dashboardStats.printingPathDistribution.rotary}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Rotary Printing
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Other prefixes
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Lead Time Histogram */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Lead Time Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Distribution of strike offs by their total lead time (time spent in active processing stages)
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar
              data={{
                labels: dashboardStats.leadTimeHistogram.map((item: any) => item.range),
                datasets: [
                  {
                    label: 'Number of Strike Offs',
                    data: dashboardStats.leadTimeHistogram.map((item: any) => item.count),
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: false,
                  },
                  datalabels: {
                    anchor: 'end',
                    align: 'top',
                    formatter: (value: any) => value,
                    font: {
                      weight: 'bold',
                      size: 12,
                    },
                    color: '#000',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y || 0;
                        const total = dashboardStats.totalStrikeOffs;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${value} strike offs (${percentage}%)`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Lead Time Range (Days)',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                    },
                    title: {
                      display: true,
                      text: 'Number of Strike Offs',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </Paper>

        {/* Section Bar Chart */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Strike Offs by Section
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Number of active strike offs in each processing section
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar
              data={{
                labels: dashboardStats.sectionWiseStats.map((item: any) => item.listName),
                datasets: [
                  {
                    label: 'Number of Strike Offs',
                    data: dashboardStats.sectionWiseStats.map((item: any) => item.cardCount),
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: false,
                  },
                  datalabels: {
                    anchor: 'end',
                    align: 'top',
                    formatter: (value: any) => value,
                    font: {
                      weight: 'bold',
                      size: 12,
                    },
                    color: '#000',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y || 0;
                        const total = dashboardStats.totalStrikeOffs;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${value} strike offs (${percentage}%)`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Section Name',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45,
                    },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                    },
                    title: {
                      display: true,
                      text: 'Number of Strike Offs',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </Paper>

        {/* Main Section Pie Chart */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Strike Offs by Main Section
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Distribution of active strike offs across main processing sections
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Pie
                  data={{
                    labels: dashboardStats.mainSectionDistribution.map((item: any) => item.mainSection),
                    datasets: [
                      {
                        label: 'Number of Strike Offs',
                        data: dashboardStats.mainSectionDistribution.map((item: any) => item.count),
                        backgroundColor: [
                          'rgba(255, 99, 132, 0.6)',
                          'rgba(54, 162, 235, 0.6)',
                          'rgba(255, 206, 86, 0.6)',
                          'rgba(75, 192, 192, 0.6)',
                          'rgba(153, 102, 255, 0.6)',
                          'rgba(255, 159, 64, 0.6)',
                          'rgba(199, 199, 199, 0.6)',
                          'rgba(83, 102, 255, 0.6)',
                        ],
                        borderColor: [
                          'rgba(255, 99, 132, 1)',
                          'rgba(54, 162, 235, 1)',
                          'rgba(255, 206, 86, 1)',
                          'rgba(75, 192, 192, 1)',
                          'rgba(153, 102, 255, 1)',
                          'rgba(255, 159, 64, 1)',
                          'rgba(199, 199, 199, 1)',
                          'rgba(83, 102, 255, 1)',
                        ],
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right' as const,
                        labels: {
                          padding: 15,
                          font: {
                            size: 12,
                          },
                        },
                      },
                      datalabels: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed || 0;
                            const total = dashboardStats.totalStrikeOffs;
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return `${context.label}: ${value} strike offs (${percentage}%)`;
                          }
                        }
                      }
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ pt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Main Section Details
                </Typography>
                <List dense>
                  {dashboardStats.mainSectionDistribution.map((item: any, index: number) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {item.mainSection}
                              </Typography>
                              <Chip 
                                label={`${item.count} (${dashboardStats.totalStrikeOffs > 0 ? ((item.count / dashboardStats.totalStrikeOffs) * 100).toFixed(1) : 0}%)`}
                                color="primary"
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Includes: {item.subSections.join(', ')}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < dashboardStats.mainSectionDistribution.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* External vs Internal Factors Overview */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> External vs Internal Factors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Comparison of strike offs waiting on external factors vs internal processing
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Pie
                  data={{
                    labels: ['External Factors', 'Internal Factors'],
                    datasets: [
                      {
                        label: 'Number of Strike Offs',
                        data: [
                          dashboardStats.externalFactorsTotal,
                          dashboardStats.internalFactorsTotal
                        ],
                        backgroundColor: [
                          'rgba(255, 99, 132, 0.6)',
                          'rgba(54, 162, 235, 0.6)',
                        ],
                        borderColor: [
                          'rgba(255, 99, 132, 1)',
                          'rgba(54, 162, 235, 1)',
                        ],
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom' as const,
                        labels: {
                          padding: 15,
                          font: {
                            size: 13,
                            weight: 'bold',
                          },
                        },
                      },
                      datalabels: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed || 0;
                            const total = dashboardStats.totalStrikeOffs;
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return `${context.label}: ${value} strike offs (${percentage}%)`;
                          }
                        }
                      }
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 3, bgcolor: '#ffebee', borderRadius: 2, border: '2px solid rgba(255, 99, 132, 0.5)' }}>
                    <Typography variant="h3" sx={{ color: '#d32f2f', fontWeight: 700 }}>
                      {dashboardStats.externalFactorsTotal}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                      External Factors
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Waiting on external parties
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 3, bgcolor: '#e3f2fd', borderRadius: 2, border: '2px solid rgba(54, 162, 235, 0.5)' }}>
                    <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700 }}>
                      {dashboardStats.internalFactorsTotal}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Internal Factors
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      In internal processing
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  External Factors Include:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  WF PFP, WF STD, CAD WF TIFF, CAD Approval Pending, Finishing in foil samples
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* External Factors Breakdown */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> External Factors Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Strike offs waiting on external parties (outside our control)
          </Typography>
          <Box sx={{ height: 350 }}>
            <Bar
              data={{
                labels: dashboardStats.externalFactors.map((item: any) => item.listName),
                datasets: [
                  {
                    label: 'Number of Strike Offs',
                    data: dashboardStats.externalFactors.map((item: any) => item.cardCount),
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  datalabels: {
                    anchor: 'end',
                    align: 'top',
                    formatter: (value: any) => value,
                    font: {
                      weight: 'bold',
                      size: 12,
                    },
                    color: '#000',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y || 0;
                        const total = dashboardStats.externalFactorsTotal;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${value} strike offs (${percentage}% of external)`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'External Factor',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                    },
                    title: {
                      display: true,
                      text: 'Number of Strike Offs',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </Paper>

        {/* Internal Factors Breakdown */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Internal Factors Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Strike offs in internal processing stages (under our control)
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar
              data={{
                labels: dashboardStats.internalFactors.map((item: any) => item.listName),
                datasets: [
                  {
                    label: 'Number of Strike Offs',
                    data: dashboardStats.internalFactors.map((item: any) => item.cardCount),
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  datalabels: {
                    anchor: 'end',
                    align: 'top',
                    formatter: (value: any) => value,
                    font: {
                      weight: 'bold',
                      size: 12,
                    },
                    color: '#000',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y || 0;
                        const total = dashboardStats.internalFactorsTotal;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${value} strike offs (${percentage}% of internal)`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Internal Processing Stage',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45,
                    },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                    },
                    title: {
                      display: true,
                      text: 'Number of Strike Offs',
                      font: {
                        weight: 'bold',
                        size: 14,
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </Paper>

        {/* Printing Path Distribution */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Digital vs Rotary Printing Path
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Distribution of strike offs by printing technology (based on card name prefix)
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Pie
                  data={{
                    labels: ['Digital Printing', 'Rotary Printing'],
                    datasets: [
                      {
                        label: 'Number of Strike Offs',
                        data: [
                          dashboardStats.printingPathDistribution.digital,
                          dashboardStats.printingPathDistribution.rotary
                        ],
                        backgroundColor: [
                          'rgba(156, 39, 176, 0.6)',
                          'rgba(255, 152, 0, 0.6)',
                        ],
                        borderColor: [
                          'rgba(156, 39, 176, 1)',
                          'rgba(255, 152, 0, 1)',
                        ],
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom' as const,
                        labels: {
                          padding: 20,
                          font: {
                            size: 14,
                            weight: 'bold',
                          },
                        },
                      },
                      datalabels: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed || 0;
                            const total = dashboardStats.totalStrikeOffs;
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return `${context.label}: ${value} strike offs (${percentage}%)`;
                          }
                        }
                      }
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box textAlign="center" sx={{ p: 3, bgcolor: '#f3e5f5', borderRadius: 2, border: '2px solid rgba(156, 39, 176, 0.5)' }}>
                    <Typography variant="h3" sx={{ color: '#7b1fa2', fontWeight: 700 }}>
                      {dashboardStats.printingPathDistribution.digital}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                      Digital Printing
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Card prefixes: PZ, PT, PGZ, PRE
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box textAlign="center" sx={{ p: 3, bgcolor: '#fff3e0', borderRadius: 2, border: '2px solid rgba(255, 152, 0, 0.5)' }}>
                    <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                      {dashboardStats.printingPathDistribution.rotary}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                      Rotary Printing
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      All other card prefixes
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Highest Lead Time Cards */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon /> Cards Exceeding 5 Days Lead Time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All cards with total lead time exceeding 5 days (sorted by highest lead time first)
          </Typography>
          {dashboardStats.cardsExceeding5Days && dashboardStats.cardsExceeding5Days.length > 0 ? (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00' }}>
                  ⚠️ {dashboardStats.cardsExceeding5Days.length} card(s) exceeding 5 days lead time
                </Typography>
              </Box>
              <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Card Name</strong></TableCell>
                  <TableCell align="center"><strong>Total Lead Time</strong></TableCell>
                  <TableCell><strong>Current List</strong></TableCell>
                  <TableCell><strong>List with Max Time</strong></TableCell>
                  <TableCell align="center"><strong>Time in Max List</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardStats.cardsExceeding5Days.map((card: any, index: number) => (
                  <TableRow 
                    key={card.cardId}
                    sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                      bgcolor: card.totalLeadTimeDays >= 10 ? '#ffebee' : '#fff3e0' // Red for 10+ days, orange for 5-10 days
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {index + 1}. {card.cardName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${card.totalLeadTimeDays}d ${card.totalLeadTimeHours}h`}
                        color={card.totalLeadTimeDays >= 10 ? 'error' : 'warning'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={card.currentListName}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={card.maxTimeListName}
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {card.maxTimeDays}d {card.maxTimeHours}h
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                ✅ No cards exceeding 5 days lead time
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  if (loading && boards.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#0079BF' }}>
          {managerSection === 'bulk' ? 'Printing Bulk Management'
            : managerSection === 'cad' ? 'Printing CAD Management'
            : 'Printing S/OFF Management'}
        </Typography>
        <Box display="flex" gap={2}>
          {/* S/OFF section action buttons */}
          {managerSection === 'soff' && selectedBoard && activeTab === 0 && dashboardStats && (
            <Button
              variant="contained"
              startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={generatePDFReport}
              disabled={pdfGenerating}
              sx={{ bgcolor: '#FF5722', '&:hover': { bgcolor: '#E64A19' } }}
            >
              {pdfGenerating ? 'Generating PDF...' : 'Download PDF Report'}
            </Button>
          )}
          {/* Bulk section PDF button */}
          {managerSection === 'bulk' && dashboardStats && dashboardStats.boardType === 'bulk' && (
            <Button
              variant="contained"
              startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={generatePDFReport}
              disabled={pdfGenerating}
              sx={{ bgcolor: '#FF5722', '&:hover': { bgcolor: '#E64A19' } }}
            >
              {pdfGenerating ? 'Generating PDF...' : 'Download PDF Report'}
            </Button>
          )}
          {managerSection === 'soff' && selectedBoard && activeTab === 1 && (
            <Button
              variant="contained"
              startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportToExcel}
              disabled={exportLoading || cards.length === 0}
              sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45a049' } }}
            >
              {exportLoading ? `Exporting... ${exportProgress}%` : 'Export to Excel'}
            </Button>
          )}
          {/* CAD section action buttons */}
          {managerSection === 'cad' && cadActiveTab === 1 && (
            <Button
              variant="contained"
              startIcon={cadExportLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleCadExportToExcel}
              disabled={cadExportLoading || cadCards.length === 0}
              sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45a049' } }}
            >
              {cadExportLoading ? `Exporting... ${cadExportProgress}%` : 'Export to Excel'}
            </Button>
          )}
          {/* Bulk Export to Excel button (Board View tab) */}
          {managerSection === 'bulk' && bulkActiveTab === 2 && (
            <Button
              variant="contained"
              startIcon={bulkExportLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleBulkExportToExcel}
              disabled={bulkExportLoading || bulkBoardCards.length === 0}
              sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45a049' } }}
            >
              {bulkExportLoading ? `Exporting... ${bulkExportProgress}%` : 'Export to Excel'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              if (managerSection === 'cad') {
                fetchCadBoard();
              } else if (managerSection === 'bulk') {
                if (bulkActiveTab === 2) {
                  fetchBulkBoardData();
                } else if (bulkActiveTab === 1) {
                  fetchBulkLeadTime();
                } else {
                  fetchDashboardStats(BULK_BOARD_ID);
                }
              } else if (selectedBoard) {
                if (activeTab === 0) {
                  fetchDashboardStats(selectedBoard.id);
                } else if (activeTab === 1) {
                  fetchBoardData(selectedBoard.id);
                }
              } else {
                fetchBoards();
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ── Section switcher: hidden — navigation handled by sidebar ── */}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ══════════ S/OFF Manager Section ══════════ */}
      {managerSection === 'soff' && (<>
        {!selectedBoard ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Select a Trello Board
            </Typography>
          </Grid>
          {boards.map((board) => (
            <Grid item xs={12} sm={6} md={4} key={board.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
                onClick={() => setSelectedBoard(board)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <BoardIcon sx={{ fontSize: 40, color: '#0079BF' }} />
                    <Box>
                      <Typography variant="h6">{board.name}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {selectedBoard.name}
                </Typography>
                {selectedBoard.desc && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedBoard.desc}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Dashboard" icon={<ReportIcon />} iconPosition="start" />
                <Tab label="Board View" icon={<BoardIcon />} iconPosition="start" />
                {canViewPrintabilityAnalytics && (
                  <Tab label="Printability" icon={<DashboardIcon />} iconPosition="start" />
                )}
              </Tabs>
            </Box>

            {/* Stats only for Board View */}
            {activeTab === 1 && stats && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#0079BF' }}>
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Cards
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#4CAF50' }}>
                      {stats.completed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#FF9800' }}>
                      {stats.withDueDate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      With Due Date
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#f44336' }}>
                      {stats.overdue}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Overdue
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>

          {/* Printability Analytics Tab */}
          {activeTab === 2 && canViewPrintabilityAnalytics && (
            <Box sx={{ mt: 3 }}>
              {/* Header */}
              <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #0079BF 0%, #005A8E 100%)' }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                  Printability Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  Cards in pipeline: Printing Office → Paste Preparation → Printing Machine / Digital Machine
                </Typography>
              </Paper>

              {/* Snapshot selector */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Select Snapshot Date &amp; Time</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4} md={3}>
                    <TextField
                      fullWidth size="small" label="Date" type="date"
                      value={snapshotDate}
                      onChange={(e) => setSnapshotDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={3}>
                    <TextField
                      fullWidth size="small" label="Time" type="time"
                      value={snapshotTime}
                      onChange={(e) => setSnapshotTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={3}>
                    <Button
                      variant="contained"
                      onClick={fetchPrintabilityAnalytics}
                      disabled={printabilityLoading}
                      sx={{ bgcolor: '#0079BF', '&:hover': { bgcolor: '#005A8E' } }}
                      fullWidth
                    >
                      {printabilityLoading ? <CircularProgress size={20} color="inherit" /> : 'Analyse'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {printabilityError && <Alert severity="error" sx={{ mb: 3 }}>{printabilityError}</Alert>}

              {printabilityLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                  <Box textAlign="center">
                    <CircularProgress sx={{ color: '#0079BF' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Fetching card histories from Trello...
                    </Typography>
                  </Box>
                </Box>
              )}

              {!printabilityLoading && printabilityData && (() => {
                const pd = printabilityData;
                const bucketColors = ['#4CAF50', '#2196F3', '#FF9800', '#f44336'];
                const bucketBg = ['#e8f5e9', '#e3f2fd', '#fff3e0', '#ffebee'];

                return (
                  <>
                    {/* Summary row */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #0079BF' }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, color: '#0079BF' }}>{pd.totalCards}</Typography>
                          <Typography variant="body2" color="text.secondary">Total Cards in Pipeline</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${pd.weightedPrintability >= 75 ? '#f44336' : pd.weightedPrintability >= 50 ? '#FF9800' : '#4CAF50'}` }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, color: pd.weightedPrintability >= 75 ? '#f44336' : pd.weightedPrintability >= 50 ? '#FF9800' : '#4CAF50' }}>
                            {pd.weightedPrintability}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Weighted Printability</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #f44336' }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, color: '#f44336' }}>
                            {pd.buckets.filter((b: any) => b.printability === 100).reduce((s: number, b: any) => s + b.count, 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Critical (72h+, 100%)</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #4CAF50' }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                            {pd.buckets[0]?.count ?? 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Fresh (0–24h, 0%)</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Snapshot info */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, pl: 0.5 }}>
                      Snapshot: {new Date(pd.snapshotTime).toLocaleString()} &nbsp;|&nbsp;
                      Pipeline lists found: {pd.pipelineListsFound?.join(', ')}
                    </Typography>

                    {/* Compact card table — moved below into collapsible */}

                    {/* Bar chart: card count per bucket */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Cards by Printability Bucket</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Distribution of {pd.totalCards} pipeline cards by age group at snapshot time
                      </Typography>
                      <Box sx={{ height: 280 }}>
                        <Bar
                          data={{
                            labels: pd.buckets.map((b: any) => b.label),
                            datasets: [{
                              label: 'Cards',
                              data: pd.buckets.map((b: any) => b.count),
                              backgroundColor: bucketColors,
                              borderRadius: 6,
                            }],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              datalabels: {
                                display: (ctx) => (ctx.dataset.data[ctx.dataIndex] as number) > 0,
                                color: 'white',
                                font: { weight: 'bold', size: 13 },
                                formatter: (v) => v,
                              },
                              tooltip: {
                                callbacks: {
                                  label: (ctx) => ` ${ctx.raw} card(s)   —   Printability: ${pd.buckets[ctx.dataIndex].printability}%`,
                                },
                              },
                            },
                            scales: {
                              x: { ticks: { font: { size: 11 } } },
                              y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Number of Cards' } },
                            },
                          }}
                        />
                      </Box>
                    </Paper>

                    {/* Doughnut: printability breakdown */}
                    {pd.totalCards > 0 && (
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Printability Composition</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Weighted printability score: <strong>{pd.weightedPrintability}%</strong>
                        </Typography>
                        <Box sx={{ height: 280, display: 'flex', justifyContent: 'center' }}>
                          <Doughnut
                            data={{
                              labels: pd.buckets.map((b: any) => `${b.label} (${b.printability}%)`),
                              datasets: [{
                                data: pd.buckets.map((b: any) => b.count),
                                backgroundColor: bucketColors,
                                borderWidth: 2,
                              }],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { position: 'right' },
                                datalabels: {
                                  display: (ctx) => (ctx.dataset.data[ctx.dataIndex] as number) > 0,
                                  color: 'white',
                                  font: { weight: 'bold', size: 13 },
                                  formatter: (v) => v,
                                },
                              },
                            }}
                          />
                        </Box>
                      </Paper>
                    )}
                  </>
                );
              })()}

              {/* ── Collapsible Card Table ── */}
              {!printabilityLoading && printabilityData && (() => {
                const pd = printabilityData;
                const bucketColors = ['#4CAF50', '#2196F3', '#FF9800', '#f44336'];
                const bucketBg = ['#e8f5e9', '#e3f2fd', '#fff3e0', '#ffebee'];
                return (
                  <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                    <Box
                      onClick={() => setCardTableOpen(o => !o)}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none', bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#eeeeee' } }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Card Details — {pd.totalCards} cards in pipeline
                      </Typography>
                      <ExpandMoreIcon sx={{ transform: cardTableOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                    </Box>
                    <Collapse in={cardTableOpen}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#555', width: '40%' }}>Card</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#555', width: '30%' }}>List</th>
                            <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#555', width: '15%' }}>Age</th>
                            <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#555', width: '15%' }}>Printability</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pd.buckets.map((bucket: any, idx: number) => (
                            bucket.count > 0 ? (
                              <React.Fragment key={bucket.label}>
                                <tr style={{ background: bucketBg[idx] }}>
                                  <td colSpan={4} style={{ padding: '5px 12px', borderLeft: `4px solid ${bucketColors[idx]}` }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.78rem', color: bucketColors[idx] }}>{bucket.label}</span>
                                    <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#666' }}>{bucket.count} card{bucket.count !== 1 ? 's' : ''} · {bucket.printability}% printability</span>
                                  </td>
                                </tr>
                                {bucket.cards.map((card: any, ci: number) => (
                                  <tr key={card.id} style={{ background: ci % 2 === 0 ? '#fff' : '#fafafa', borderLeft: `4px solid ${bucketColors[idx]}` }}>
                                    <td style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 500 }}>
                                      <a href={card.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                                        {card.name}
                                      </a>
                                    </td>
                                    <td style={{ padding: '5px 12px', fontSize: '0.75rem', color: '#555' }}>{card.currentList}</td>
                                    <td style={{ padding: '5px 12px', fontSize: '0.75rem', color: '#555', textAlign: 'center', whiteSpace: 'nowrap' }}>{card.ageDays}d {card.ageRemainingHours}h</td>
                                    <td style={{ padding: '5px 12px', textAlign: 'center' }}>
                                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, background: bucketColors[idx], color: '#fff' }}>{bucket.printability}%</span>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ) : null
                          ))}
                        </tbody>
                      </table>
                    </Collapse>
                  </Paper>
                );
              })()}

              {/* ── Trend Section ── */}
              <Paper sx={{ p: 3, mt: 3, mb: 2, background: 'linear-gradient(135deg, #37474f 0%, #263238 100%)' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>Printability Trend</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  Pipeline card counts by printability bucket — one data point per day at the selected time of day
                </Typography>
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Select Date Range &amp; Time of Day</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3} md={2}>
                    <TextField fullWidth size="small" label="From" type="date"
                      value={trendStartDate} onChange={(e) => setTrendStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={2}>
                    <TextField fullWidth size="small" label="To" type="date"
                      value={trendEndDate} onChange={(e) => setTrendEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={2}>
                    <TextField fullWidth size="small" label="Time of day" type="time"
                      value={trendTime} onChange={(e) => setTrendTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Same as snapshot time to compare"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={2}>
                    <Button variant="contained" onClick={fetchPrintabilityTrend} disabled={trendLoading}
                      sx={{ bgcolor: '#37474f', '&:hover': { bgcolor: '#263238' } }} fullWidth>
                      {trendLoading ? <CircularProgress size={20} color="inherit" /> : 'Plot Trend'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {trendError && <Alert severity="error" sx={{ mb: 3 }}>{trendError}</Alert>}

              {trendLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <Box textAlign="center">
                    <CircularProgress sx={{ color: '#37474f' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Fetching card histories across date range…
                    </Typography>
                  </Box>
                </Box>
              )}

              {!trendLoading && trendData && (() => {
                const labels = trendData.trendData.map((d: any) => {
                  const [y, m, day] = d.date.split('-');
                  return `${day}/${m}`;
                });
                const d0   = trendData.trendData.map((d: any) => d.counts[0]   ?? 0);
                const d50  = trendData.trendData.map((d: any) => d.counts[50]  ?? 0);
                const d75  = trendData.trendData.map((d: any) => d.counts[75]  ?? 0);
                const d100 = trendData.trendData.map((d: any) => d.counts[100] ?? 0);
                const total = trendData.trendData.map((d: any) => d.total);

                const sharedDatasets = [
                  { label: '0% (0–24h)',  data: d0,   borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.15)',  fill: true, tension: 0.3, pointRadius: 4 },
                  { label: '50% (24–48h)', data: d50,  borderColor: '#2196F3', backgroundColor: 'rgba(33,150,243,0.12)', fill: true, tension: 0.3, pointRadius: 4 },
                  { label: '75% (48–72h)', data: d75,  borderColor: '#FF9800', backgroundColor: 'rgba(255,152,0,0.12)',  fill: true, tension: 0.3, pointRadius: 4 },
                  { label: '100% (72h+)',  data: d100, borderColor: '#f44336', backgroundColor: 'rgba(244,67,54,0.12)',  fill: true, tension: 0.3, pointRadius: 4 },
                ];

                const sharedOptions: any = {
                  responsive: true, maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' as const },
                    datalabels: { display: false },
                    tooltip: { mode: 'index' as const, intersect: false },
                  },
                  scales: {
                    x: { ticks: { maxRotation: 45, font: { size: 10 } } },
                    y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Number of Cards' } },
                  },
                  interaction: { mode: 'index' as const, intersect: false },
                };

                return (
                  <>
                    {/* Line chart */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Card Count per Bucket — Line</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {trendData.trendData.length} days · {trendData.startDate} → {trendData.endDate}
                      </Typography>
                      <Box sx={{ height: 320 }}>
                        <Line data={{ labels, datasets: sharedDatasets }} options={sharedOptions} />
                      </Box>
                    </Paper>

                    {/* Stacked bar chart */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Card Count per Bucket — Stacked Bar</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Total pipeline cards per day with printability breakdown
                      </Typography>
                      <Box sx={{ height: 320 }}>
                        <Bar
                          data={{
                            labels,
                            datasets: [
                              { label: '0% (0–24h)',   data: d0,   backgroundColor: '#4CAF50', stack: 's' },
                              { label: '50% (24–48h)', data: d50,  backgroundColor: '#2196F3', stack: 's' },
                              { label: '75% (48–72h)', data: d75,  backgroundColor: '#FF9800', stack: 's' },
                              { label: '100% (72h+)',  data: d100, backgroundColor: '#f44336', stack: 's' },
                            ],
                          }}
                          options={{
                            responsive: true, maintainAspectRatio: false,
                            plugins: {
                              legend: { position: 'top' as const },
                              datalabels: { display: false },
                              tooltip: { mode: 'index' as const, intersect: false,
                                callbacks: { footer: (items) => `Total: ${items.reduce((s,i) => s + (i.raw as number), 0)}` }
                              },
                            },
                            scales: {
                              x: { stacked: true, ticks: { maxRotation: 45, font: { size: 10 } } },
                              y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Number of Cards' } },
                            },
                          }}
                        />
                      </Box>
                    </Paper>

                    {/* Weighted printability trend line */}
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Weighted Printability % — Daily Trend</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Average printability score across all pipeline cards per day
                      </Typography>
                      <Box sx={{ height: 260 }}>
                        <Line
                          data={{
                            labels,
                            datasets: [{
                              label: 'Weighted Printability %',
                              data: trendData.trendData.map((d: any) => {
                                if (!d.total) return null;
                                return Math.round(((d.counts[0]??0)*0 + (d.counts[50]??0)*50 + (d.counts[75]??0)*75 + (d.counts[100]??0)*100) / d.total);
                              }),
                              borderColor: '#f44336',
                              backgroundColor: 'rgba(244,67,54,0.1)',
                              fill: true,
                              tension: 0.3,
                              pointRadius: 4,
                              spanGaps: true,
                            }],
                          }}
                          options={{
                            responsive: true, maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              datalabels: { display: false },
                              tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw}%` } },
                            },
                            scales: {
                              x: { ticks: { maxRotation: 45, font: { size: 10 } } },
                              y: { beginAtZero: true, max: 100,
                                ticks: { callback: (v) => `${v}%` },
                                title: { display: true, text: 'Weighted Printability (%)' },
                              },
                            },
                          }}
                        />
                      </Box>
                    </Paper>
                  </>
                );
              })()}
            </Box>
          )}

          {/* Render tab content */}
          {activeTab === 1 ? (
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                pb: 2,
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#0079BF',
                  borderRadius: '10px',
                  '&:hover': {
                    backgroundColor: '#026AA7',
                  },
                },
              }}
            >
              {lists.map((list) => (
                <Box key={list.id} sx={{ minWidth: '320px', maxWidth: '320px' }}>
                  <Paper sx={{ p: 2, minHeight: 400 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {list.name}
                      </Typography>
                      <Chip
                        label={getCardsForList(list.id).length}
                        size="small"
                        sx={{ bgcolor: '#0079BF', color: 'white' }}
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <List>
                      {getCardsForList(list.id).map((card) => (
                        <ListItem
                          key={card.id}
                          onClick={() => handleCardClick(card)}
                          sx={{
                            mb: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: '#f5f5f5',
                              boxShadow: 2,
                            },
                          }}
                        >
                          <Box width="100%">
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <ListItemText
                                primary={card.name}
                                secondary={card.desc}
                                primaryTypographyProps={{ fontWeight: 600 }}
                              />
                              <IconButton
                                size="small"
                                href={card.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            {card.labels && card.labels.length > 0 && (
                              <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                                {card.labels.map((label) => (
                                  <Chip
                                    key={label.id}
                                    label={label.name || label.color}
                                    size="small"
                                    sx={{
                                      bgcolor: label.color || '#ccc',
                                      color: 'white',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                            {card.due && (
                              <Box display="flex" alignItems="center" gap={1} mt={1}>
                                {card.dueComplete ? (
                                  <DoneIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                ) : (
                                  <PendingIcon sx={{ fontSize: 16, color: '#FF9800' }} />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  Due: {new Date(card.due).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              ))}
            </Box>
          ) : activeTab === 0 ? (
            dashboardStats && dashboardStats.boardType === 'bulk' ? renderBulkDashboard() : renderDashboard()
          ) : null}
        </>
      )}
      </>)}  {/* end managerSection === 'soff' */}

      {/* ══════════ Bulk Management Section ══════════ */}
      {managerSection === 'bulk' && (
        <>
          {/* Bulk Tab Navigation */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>Printing Bulk Management</Typography>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
              <Tabs value={bulkActiveTab} onChange={(_e, v) => {
                setBulkActiveTab(v);
                if (v === 1 && !bulkLeadTimeData && !bulkLeadTimeLoading) {
                  fetchBulkLeadTime();
                }
                if (v === 2 && bulkBoardLists.length === 0) {
                  fetchBulkBoardData();
                }
              }}>
                <Tab label="Dashboard" icon={<ReportIcon />} iconPosition="start" />
                <Tab label="Lead Time" icon={<HistoryIcon />} iconPosition="start" />
                <Tab label="Board View" icon={<BoardIcon />} iconPosition="start" />
              </Tabs>
            </Box>
            {/* Stats bar for Board View */}
            {bulkActiveTab === 2 && bulkStats && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#0079BF' }}>{bulkStats.total}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Cards</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#4CAF50' }}>{bulkStats.completed}</Typography>
                    <Typography variant="body2" color="text.secondary">Completed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#FF9800' }}>{bulkStats.withDueDate}</Typography>
                    <Typography variant="body2" color="text.secondary">With Due Date</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#f44336' }}>{bulkStats.overdue}</Typography>
                    <Typography variant="body2" color="text.secondary">Overdue</Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>

          {/* Bulk Dashboard Tab */}
          {bulkActiveTab === 0 && (
            dashboardLoading ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="300px" gap={2}>
                <CircularProgress />
                <Typography color="text.secondary">{dashboardMessage || 'Loading Bulk Management data…'}</Typography>
              </Box>
            ) : dashboardStats && dashboardStats.boardType === 'bulk' ? (
              renderBulkDashboard()
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={4}>
                <Typography color="text.secondary">No data loaded yet.</Typography>
                <Button variant="contained" onClick={() => fetchDashboardStats(BULK_BOARD_ID)}>Load Bulk Dashboard</Button>
              </Box>
            )
          )}

          {/* Bulk Lead Time Tab */}
          {bulkActiveTab === 1 && renderBulkLeadTimeDashboard()}

          {/* Bulk Board View Tab — identical design to S/OFF board view */}
          {bulkActiveTab === 2 && (
            bulkBoardLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  overflowX: 'auto',
                  pb: 2,
                  '&::-webkit-scrollbar': { height: '8px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '10px' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#0079BF',
                    borderRadius: '10px',
                    '&:hover': { backgroundColor: '#026AA7' },
                  },
                }}
              >
                {bulkBoardLists.map((list) => (
                  <Box key={list.id} sx={{ minWidth: '320px', maxWidth: '320px' }}>
                    <Paper sx={{ p: 2, minHeight: 400 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{list.name}</Typography>
                        <Chip
                          label={bulkBoardCards.filter(c => c.idList === list.id).length}
                          size="small"
                          sx={{ bgcolor: '#0079BF', color: 'white' }}
                        />
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <List>
                        {bulkBoardCards.filter(c => c.idList === list.id).map((card) => (
                          <ListItem
                            key={card.id}
                            onClick={() => handleCardClick(card)}
                            sx={{
                              mb: 1,
                              bgcolor: 'background.paper',
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#f5f5f5', boxShadow: 2 },
                            }}
                          >
                            <Box width="100%">
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <ListItemText
                                  primary={card.name}
                                  secondary={card.desc}
                                  primaryTypographyProps={{ fontWeight: 600 }}
                                />
                                <IconButton
                                  size="small"
                                  href={card.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {card.labels && card.labels.length > 0 && (
                                <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                                  {card.labels.map((label) => (
                                    <Chip
                                      key={label.id}
                                      label={label.name || label.color}
                                      size="small"
                                      sx={{ bgcolor: label.color || '#ccc', color: 'white', fontSize: '0.7rem' }}
                                    />
                                  ))}
                                </Box>
                              )}
                              {card.due && (
                                <Box display="flex" alignItems="center" gap={1} mt={1}>
                                  {card.dueComplete ? (
                                    <DoneIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                  ) : (
                                    <PendingIcon sx={{ fontSize: 16, color: '#FF9800' }} />
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    Due: {new Date(card.due).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Box>
                ))}
              </Box>
            )
          )}
        </>
      )}

      {/* ══════════ CAD Manager Section ══════════ */}
      {managerSection === 'cad' && (
        <>
          {cadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCadError('')}>{cadError}</Alert>
          )}
          {cadLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading CAD Design board…</Typography>
            </Box>
          ) : (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#0079BF' }}>
                    CAD Design Board
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cadCards.length} active cards across {cadLists.length} lists
                  </Typography>
                </Box>
              </Box>

              {/* CAD Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={cadActiveTab} onChange={(_e, v) => setCadActiveTab(v)}>
                  <Tab label="Dashboard" icon={<ReportIcon />} iconPosition="start" />
                  <Tab label="Board View" icon={<BoardIcon />} iconPosition="start" />
                </Tabs>
              </Box>

              {/* CAD Stats Bar (Board View only) */}
              {cadActiveTab === 1 && (() => {
                const s = getCadStats();
                return (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" sx={{ color: '#0079BF' }}>{s.total}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Cards</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" sx={{ color: '#4CAF50' }}>{s.completed}</Typography>
                        <Typography variant="body2" color="text.secondary">Completed</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" sx={{ color: '#FF9800' }}>{s.withDueDate}</Typography>
                        <Typography variant="body2" color="text.secondary">With Due Date</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" sx={{ color: '#f44336' }}>{s.overdue}</Typography>
                        <Typography variant="body2" color="text.secondary">Overdue</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })()}

              {/* ─ CAD Dashboard Tab ─ */}
              {cadActiveTab === 0 && (
                <Box>
                  {/* Cards per list bar chart summary */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {cadLists.map((list) => {
                      const count = cadCards.filter((c) => c.idList === list.id).length;
                      return (
                        <Grid item xs={6} sm={4} md={3} key={list.id}>
                          <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0079BF' }}>
                              {count}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                              {list.name}
                            </Typography>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Bar chart */}
                  {cadLists.length > 0 && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Cards per List</Typography>
                      <Box sx={{ height: 300 }}>
                        <Bar
                          data={{
                            labels: cadLists.map((l) => l.name),
                            datasets: [{
                              label: 'Cards',
                              data: cadLists.map((l) => cadCards.filter((c) => c.idList === l.id).length),
                              backgroundColor: '#0079BF',
                              borderRadius: 4,
                            }],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              datalabels: {
                                anchor: 'end', align: 'top',
                                formatter: (v: number) => v || '',
                                font: { weight: 'bold' as const },
                              },
                            },
                            scales: {
                              x: { ticks: { maxRotation: 45, font: { size: 11 } } },
                              y: { beginAtZero: true, ticks: { stepSize: 1 } },
                            },
                          }}
                        />
                      </Box>
                    </Paper>
                  )}

                  {/* Table: all cards with list */}
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>All Cards</Typography>
                    <TableContainer sx={{ maxHeight: 500 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Card Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>List / Stage</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Due Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Link</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cadCards.map((card) => {
                            const listName = cadLists.find((l) => l.id === card.idList)?.name || 'Unknown';
                            const isOverdue = card.due && !card.dueComplete && new Date(card.due) < new Date();
                            return (
                              <TableRow
                                key={card.id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleCadCardClick(card)}
                              >
                                <TableCell sx={{ fontWeight: 600 }}>{card.name}</TableCell>
                                <TableCell>
                                  <Chip label={listName} size="small" sx={{ bgcolor: '#e3f2fd', color: '#0d47a1' }} />
                                </TableCell>
                                <TableCell>
                                  {card.due ? new Date(card.due).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell>
                                  {card.dueComplete ? (
                                    <Chip label="Done" size="small" color="success" />
                                  ) : isOverdue ? (
                                    <Chip label="Overdue" size="small" color="error" />
                                  ) : card.due ? (
                                    <Chip label="Pending" size="small" color="warning" />
                                  ) : (
                                    <Chip label="In Progress" size="small" sx={{ bgcolor: '#e3f2fd' }} />
                                  )}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <IconButton size="small" href={card.url} target="_blank" rel="noopener noreferrer">
                                    <LinkIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              )}

              {/* ─ CAD Board View Tab ─ */}
              {cadActiveTab === 1 && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Search cards…"
                      value={cadSearch}
                      onChange={(e) => setCadSearch(e.target.value)}
                      sx={{ width: 280 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      overflowX: 'auto',
                      pb: 2,
                      '&::-webkit-scrollbar': { height: '8px' },
                      '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '10px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#0079BF', borderRadius: '10px', '&:hover': { backgroundColor: '#026AA7' } },
                    }}
                  >
                    {cadLists.map((list) => (
                      <Box key={list.id} sx={{ minWidth: '300px', maxWidth: '300px' }}>
                        <Paper sx={{ p: 2, minHeight: 400 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>{list.name}</Typography>
                            <Chip
                              label={getCadCardsForList(list.id).length}
                              size="small"
                              sx={{ bgcolor: '#0079BF', color: 'white' }}
                            />
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <List disablePadding>
                            {getCadCardsForList(list.id).map((card) => (
                              <ListItem
                                key={card.id}
                                onClick={() => handleCadCardClick(card)}
                                sx={{
                                  mb: 1,
                                  bgcolor: 'background.paper',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1,
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: '#f5f5f5', boxShadow: 2 },
                                }}
                              >
                                <Box width="100%">
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    <ListItemText
                                      primary={card.name}
                                      secondary={card.desc}
                                      primaryTypographyProps={{ fontWeight: 600 }}
                                    />
                                    <IconButton size="small" href={card.url} target="_blank" rel="noopener noreferrer">
                                      <LinkIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  {card.labels && card.labels.length > 0 && (
                                    <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                                      {card.labels.map((label) => (
                                        <Chip
                                          key={label.id}
                                          label={label.name || label.color}
                                          size="small"
                                          sx={{ bgcolor: label.color || '#ccc', color: 'white', fontSize: '0.7rem' }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                  {card.due && (
                                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                                      {card.dueComplete ? (
                                        <DoneIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                      ) : (
                                        <PendingIcon sx={{ fontSize: 16, color: '#FF9800' }} />
                                      )}
                                      <Typography variant="caption" color="text.secondary">
                                        Due: {new Date(card.due).toLocaleDateString()}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </ListItem>
                            ))}
                            {getCadCardsForList(list.id).length === 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                No cards
                              </Typography>
                            )}
                          </List>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          )}
        </>
      )}

      {/* ══════════ Card History Dialog (S/OFF) ══════════ */}
      <Dialog
        open={selectedCard !== null}
        onClose={() => setSelectedCard(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon />
            <Typography variant="h6">Card History</Typography>
          </Box>
          {selectedCard && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedCard.name}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Created:</strong> {cardCreatedDate}
                </Typography>
                <Typography variant="body2">
                  <strong>Current List:</strong> {cardCurrentList}
                </Typography>
              </Box>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : calculateTimeInLists().length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="text.secondary">
                No list movement history found for this card.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>List</strong></TableCell>
                    <TableCell><strong>Days</strong></TableCell>
                    <TableCell><strong>Hours</strong></TableCell>
                    <TableCell><strong>Times Visited</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculateTimeInLists().map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.listName}</TableCell>
                      <TableCell>{item.days}</TableCell>
                      <TableCell>{item.hours}</TableCell>
                      <TableCell>{item.entries}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCard(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ══════════ CAD Card History Dialog ══════════ */}
      <Dialog
        open={cadSelectedCard !== null}
        onClose={() => setCadSelectedCard(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon />
            <Typography variant="h6">CAD Card History</Typography>
          </Box>
          {cadSelectedCard && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {cadSelectedCard.name}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Created:</strong> {cadCardCreatedDate || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>Current List:</strong> {cadCardCurrentList}
                </Typography>
              </Box>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {cadHistoryLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : calculateCadTimeInLists().length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="text.secondary">
                No list movement history found for this card.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>List</strong></TableCell>
                    <TableCell><strong>Days</strong></TableCell>
                    <TableCell><strong>Hours</strong></TableCell>
                    <TableCell><strong>Times Visited</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculateCadTimeInLists().map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.listName}</TableCell>
                      <TableCell>{item.days}</TableCell>
                      <TableCell>{item.hours}</TableCell>
                      <TableCell>{item.entries}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCadSelectedCard(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PrintingProcessManager;
